import { supabase } from "../../lib/supabase";
import { fetchManufacturerOptions, type ManufacturerOption } from "../stock/api";
import { DOCUMENT_PROFILE, compressImage } from "../../lib/images";
import { classifyEquipmentPhysicality, isSchematicNodePhysicality, type EquipmentPhysicality } from "./physicality";

export { fetchManufacturerOptions };
export type { ManufacturerOption };

/* ---------------------------------------------------------------- types */

export interface JobListRow {
  id: number;
  name: string;
  project_reference: string;
  job_number: string | null;
  status: string;
  company_id: number;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  pm_name: string | null;
  target_completion: string | null;
}

export interface JobDetail extends JobListRow {
  full_address: string | null;
  site_contact_name: string | null;
  site_contact_phone: string | null;
  start_date: string | null;
  target_installation_date: string | null;
  actual_completion_date: string | null;
  first_fix_days: number | null;
  second_fix_days: number | null;
  commissioning_days: number | null;
  client_po: string | null;
  notes: string | null;
}

export interface EquipmentSerial {
  id: number;
  equipment_id: number;
  serial_number: string;
  mac_address: string | null;
  recorded_at: string;
}

export type NetworkScope = "client_network" | "viewpoint_av_network" | "viewpoint_av_network_standalone" | "no_network";

export interface EquipmentNetworkInterface {
  id: number;
  equipment_id: number;
  label: string;
  ip_address: string | null;
  mac_address: string | null;
  hostname: string | null;
  network_scope: NetworkScope;
  notes: string | null;
  sort_order: number;
  created_at: string;
}

export type CommissioningStatus = "not_started" | "in_progress" | "commissioned" | "not_required";

/** Controlled vocabulary for "what a device DOES" (0155) -- distinct from
 * the Product Library's free-text typical_role and the narrower
 * network_role (which IP-autogen block). Drives both the schematic
 * topology engine and commissioning eligibility/field selection. */
export type EquipmentRole =
  | "uc_compute" | "uc_codec" | "video_bar" | "touch_controller" | "control_processor"
  | "touch_panel" | "dsp" | "microphone" | "camera" | "speaker" | "amplifier" | "display"
  | "projector" | "video_switcher" | "matrix" | "av_over_ip_encoder" | "av_over_ip_decoder"
  | "signage_player" | "network_switch" | "network_device" | "usb_extender" | "hdmi_extender"
  | "control_interface" | "wireless_presentation" | "recording_device" | "streaming_device"
  | "lighting_controller" | "power_device" | "other";

export interface JobFloor {
  id: number;
  name: string;
  sort_order: number;
}

export interface JobRoom {
  id: number;
  job_floor_id: number | null;
  name: string;
  room_type: string | null;
  sort_order: number;
  master_job_room_id: number | null;
  overridden: boolean;
}

export interface EquipmentRequirement {
  id: number;
  job_id: number;
  room: string | null;
  job_room_id: number | null;
  category: string | null;
  description: string;
  supplier: string | null;
  qty_required: number;
  qty_ordered: number;
  qty_received: number;
  order_reference: string | null;
  order_date: string | null;
  is_held: boolean;
  hold_reason: string | null;
  manufacturer_id: number | null;
  manufacturer_name: string | null;
  model_no: string | null;
  supplier_part_number: string | null;
  av_product_profile_id: number | null;
  requires_commissioning: boolean;
  commissioned: boolean;
  commissioned_at: string | null;
  commissioning_status: CommissioningStatus;
  commissioning_started_at: string | null;
  assigned_engineer_id: string | null;
  commissioning_notes: string | null;
  is_mtr_device: boolean;
  mtr_updated: boolean;
  needs_control_ids: boolean;
  control_ids: Record<string, string | null>;
  serials: EquipmentSerial[];
  network_interfaces: EquipmentNetworkInterface[];
  /** Product Library knowledge (0155) -- what this product IS/does, used to
   * decide which commissioning fields are even relevant. Null when the
   * item has no linked profile or the profile hasn't been classified yet. */
  equipment_role: EquipmentRole | null;
  default_network_requirement: string | null;
  profile_attributes: Record<string, unknown>;
  /** Rack-planning metadata (0185) -- null when the item has no linked
   * profile, not when the profile simply hasn't recorded these fields yet
   * (those come through as nulls inside the object, not as a null object). */
  /** Where this has been said to go. Null until somebody places it. */
  location_type: string | null;
  rack_meta: {
    is_rack_mountable: boolean;
    /** A bracket is wall-mountable and nothing else -- the pair of flags
     * is what tells a mount from a box that happens to hang on a wall. */
    is_wall_mountable: boolean;
    /** Set only on a rack. A rack does not go inside a rack. */
    rack_capacity_u: number | null;
    rack_unit_size: number | null;
    power_connections_230v: number | null;
    weight_kg: number | null;
    thermal_btu_typical: number | null;
    thermal_data_source: "manufacturer" | "estimated" | "unknown" | null;
  } | null;
}

/* --------------------------------------------------------------- jobs */

export async function fetchJobs(): Promise<JobListRow[]> {
  const { data, error } = await supabase.from("v_jobs_list").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as JobListRow[];
}

export async function fetchJobDetail(jobId: number): Promise<JobDetail> {
  const { data, error } = await supabase.from("v_jobs_list").select("*").eq("id", jobId).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Job not found");

  const { data: job, error: jobErr } = await supabase
    .from("jobs")
    .select(
      "full_address, site_contact_name, site_contact_phone, start_date, target_installation_date, actual_completion_date, first_fix_days, second_fix_days, commissioning_days, client_po, notes",
    )
    .eq("id", jobId)
    .single();
  if (jobErr) throw jobErr;

  return { ...(data as JobListRow), ...job } as JobDetail;
}

/* ---------------------------------------------------------- equipment */

export async function fetchJobEquipment(jobId: number): Promise<EquipmentRequirement[]> {
  const [reqRes, serialRes, nicRes] = await Promise.all([
    supabase
      .from("job_equipment_requirements")
      .select(
        "id, job_id, room, job_room_id, description, supplier, qty_required, qty_ordered, qty_received, order_reference, order_date, is_held, hold_reason, manufacturer_id, model_no, supplier_part_number, av_product_profile_id, requires_commissioning, commissioned, commissioned_at, commissioning_status, commissioning_started_at, assigned_engineer_id, commissioning_notes, is_mtr_device, mtr_updated, needs_control_ids, control_ids, location_type, av_product_profiles(category, equipment_role, default_network_requirement, attributes, is_rack_mountable, is_wall_mountable, rack_capacity_u, rack_unit_size, power_connections_230v, weight_kg, thermal_btu_typical, thermal_data_source), products(category), manufacturers(name)",
      )
      .eq("job_id", jobId)
      .order("sort_order"),
    supabase
      .from("job_equipment_serials")
      .select("id, equipment_id, serial_number, mac_address, recorded_at")
      .order("recorded_at"),
    supabase
      .from("job_equipment_network_interfaces")
      .select("id, equipment_id, label, ip_address, mac_address, hostname, network_scope, notes, sort_order, created_at")
      .order("sort_order"),
  ]);
  if (reqRes.error) throw reqRes.error;
  if (serialRes.error) throw serialRes.error;
  if (nicRes.error) throw nicRes.error;

  const byEquipment = new Map<number, EquipmentSerial[]>();
  for (const s of (serialRes.data ?? []) as EquipmentSerial[]) {
    const list = byEquipment.get(s.equipment_id) ?? [];
    list.push(s);
    byEquipment.set(s.equipment_id, list);
  }

  const nicsByEquipment = new Map<number, EquipmentNetworkInterface[]>();
  for (const n of (nicRes.data ?? []) as EquipmentNetworkInterface[]) {
    const list = nicsByEquipment.get(n.equipment_id) ?? [];
    list.push(n);
    nicsByEquipment.set(n.equipment_id, list);
  }

  return (reqRes.data ?? []).map((r) => {
    const row = r as unknown as EquipmentRequirement & {
      av_product_profiles: {
        category: string; equipment_role: EquipmentRole | null; default_network_requirement: string | null; attributes: Record<string, unknown> | null;
        is_rack_mountable: boolean | null; is_wall_mountable: boolean | null;
        rack_capacity_u: number | null; rack_unit_size: number | null; power_connections_230v: number | null;
        weight_kg: number | null; thermal_btu_typical: number | null; thermal_data_source: "manufacturer" | "estimated" | "unknown" | null;
      } | null;
      products: { category: string } | null;
      manufacturers: { name: string } | null;
    };
    const profile = row.av_product_profiles;
    return {
      ...row,
      manufacturer_name: row.manufacturers?.name ?? null,
      category: profile?.category ?? row.products?.category ?? null,
      equipment_role: profile?.equipment_role ?? null,
      default_network_requirement: profile?.default_network_requirement ?? null,
      profile_attributes: profile?.attributes ?? {},
      rack_meta: profile
        ? {
            is_rack_mountable: profile.is_rack_mountable ?? false,
            is_wall_mountable: profile.is_wall_mountable ?? false,
            rack_capacity_u: profile.rack_capacity_u,
            rack_unit_size: profile.rack_unit_size,
            power_connections_230v: profile.power_connections_230v,
            weight_kg: profile.weight_kg,
            thermal_btu_typical: profile.thermal_btu_typical,
            thermal_data_source: profile.thermal_data_source,
          }
        : null,
      control_ids: (row.control_ids ?? {}) as Record<string, string | null>,
      serials: byEquipment.get(row.id) ?? [],
      network_interfaces: nicsByEquipment.get(row.id) ?? [],
    };
  }) as EquipmentRequirement[];
}

/** Whether this device's own management interface can even be reached
 * over the network -- an unmanaged switch (or any device with no known
 * network identity) is correctly excluded, matching the Product Library's
 * own knowledge rather than "it has an RJ45 port somewhere". */
export function deviceHasNetworkIdentity(item: Pick<EquipmentRequirement, "equipment_role" | "default_network_requirement" | "profile_attributes">): boolean {
  if (item.equipment_role === "network_switch") {
    return item.profile_attributes?.is_managed_switch === true;
  }
  if (item.default_network_requirement === "client_lan") return true;
  const networkedRoles: EquipmentRole[] = [
    "uc_compute", "uc_codec", "control_processor", "touch_controller", "touch_panel", "dsp",
    "av_over_ip_encoder", "av_over_ip_decoder", "signage_player", "video_switcher", "matrix",
    "wireless_presentation", "recording_device", "streaming_device", "lighting_controller", "network_device",
  ];
  return item.equipment_role != null && networkedRoles.includes(item.equipment_role);
}

/* --------------------------------------------------- network profile (0155) */

export interface JobNetworkProfile {
  id: number;
  job_id: number;
  name: string;
  vlan_id: number | null;
  subnet_cidr: string;
  gateway: string | null;
  dns: string | null;
  dhcp_policy: "dhcp" | "static" | "reserved" | "mixed" | null;
  av_range_start: string | null;
  av_range_end: string | null;
  notes: string | null;
}

/** Null when no PM has ever defined an addressing scheme for this job --
 * that is the normal, expected state for most jobs, not an error. */
export async function fetchJobNetworkProfile(jobId: number): Promise<JobNetworkProfile | null> {
  const { data, error } = await supabase.from("job_network_profiles").select("*").eq("job_id", jobId).maybeSingle();
  if (error) throw error;
  return data as JobNetworkProfile | null;
}

export interface JobNetworkProfileInput {
  name: string;
  vlan_id: number | null;
  subnet_cidr: string;
  gateway: string | null;
  dns: string | null;
  dhcp_policy: "dhcp" | "static" | "reserved" | "mixed" | null;
  av_range_start: string | null;
  av_range_end: string | null;
  notes: string | null;
}

/** The one deliberate way a PM defines a job's addressing scheme -- nothing
 * else creates this row. `existingId` set means "edit the scheme already
 * on record" (never touches created_by); unset means "define one for the
 * first time" (stamps created_by, everything else defaults null/off until
 * this call). See job_network_profiles (0155) -- app.job_network_autogenerate
 * refuses to suggest anything until a row like this exists. */
export async function saveJobNetworkProfile(
  jobId: number,
  input: JobNetworkProfileInput,
  existingId: number | null,
  userId: string | null,
): Promise<JobNetworkProfile> {
  if (existingId) {
    const { data, error } = await supabase.from("job_network_profiles").update(input).eq("id", existingId).select("*").single();
    if (error) throw error;
    return data as JobNetworkProfile;
  }
  const { data, error } = await supabase
    .from("job_network_profiles")
    .insert({ job_id: jobId, created_by: userId, ...input })
    .select("*")
    .single();
  if (error) throw error;
  return data as JobNetworkProfile;
}

/** Floor -> Room structure for a job (0150), carried over from the quote
 * at quote_mark_won time. Jobs from before that migration (or imported
 * directly, e.g. Service's Glide-migrated jobs) fall back to a single
 * "(no floor recorded)" bucket of rooms with job_floor_id null -- see the
 * one-time backfill in that migration. */
export async function fetchJobRoomsAndFloors(jobId: number): Promise<{ floors: JobFloor[]; rooms: JobRoom[] }> {
  const [{ data: floors, error: fErr }, { data: rooms, error: rErr }] = await Promise.all([
    supabase.from("job_floors").select("id, name, sort_order").eq("job_id", jobId).order("sort_order"),
    supabase.from("job_rooms")
      .select("id, job_floor_id, name, room_type, sort_order, master_job_room_id, overridden")
      .eq("job_id", jobId).order("sort_order"),
  ]);
  if (fErr) throw fErr;
  if (rErr) throw rErr;
  return { floors: (floors ?? []) as JobFloor[], rooms: (rooms ?? []) as JobRoom[] };
}

/* ------------------------------------------------------------ commissioning */

/** Manual override for anything the quote_mark_won heuristic missed. */
export async function setRequiresCommissioning(equipmentId: number, value: boolean): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ requires_commissioning: value }).eq("id", equipmentId);
  if (error) throw error;
}

/** RPC, not a plain client write: marking commissioned also syncs the
 * as-built IP/hostname/control-IDs into the linked stock_items row(s) --
 * see app.job_equipment_mark_commissioned (0114) -- so nobody has to
 * re-key commissioning data into Service later. */
export async function setEquipmentCommissioned(equipmentId: number, commissioned: boolean): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_mark_commissioned", { p_equipment_id: equipmentId, p_commissioned: commissioned });
  if (error) throw error;
}

export async function setMtrUpdated(equipmentId: number, updated: boolean): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ mtr_updated: updated }).eq("id", equipmentId);
  if (error) throw error;
}

/** Unlike mtr_updated (gated behind is_mtr_device), this applies to any
 * commissioning item -- a plain "was the firmware updated" checkbox. */
export async function setFirmwareUpdated(equipmentId: number, updated: boolean): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ firmware_updated: updated }).eq("id", equipmentId);
  if (error) throw error;
}

/** Explicit "not required" state (0155), mutually exclusive with
 * commissioned -- an RPC because it also clears any prior commissioned
 * fields server-side, rather than trusting two separate client writes to
 * land consistently. */
export async function setEquipmentNotRequired(equipmentId: number, value: boolean): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_set_not_required", { p_equipment_id: equipmentId, p_value: value });
  if (error) throw error;
}

/** Commissioning progress (0157) -- 0-100, the single source of truth for
 * how far through commissioning an item is. Always editable in either
 * direction; 100% is "currently considered commissioned", never locked.
 * Setting a percentage also clears not_required server-side (an explicit
 * percentage is a signal the item is required again). */
export async function setEquipmentProgress(
  equipmentId: number,
  progressPercent: number,
  note?: string | null,
): Promise<{ progress_percent: number; stock_synced: boolean }> {
  const { data, error } = await supabase.rpc("job_equipment_set_progress", {
    p_equipment_id: equipmentId,
    p_progress_percent: progressPercent,
    p_note: note ?? null,
  });
  if (error) throw error;
  return data as { progress_percent: number; stock_synced: boolean };
}

export interface CommissioningNetworkInterfaceSummary {
  id: number;
  label: string;
  ip_address: string | null;
  suggested_ip: string | null;
  mac_address: string | null;
  hostname: string | null;
  assignment_mode: "static" | "dhcp" | "reserved";
  ip_source: "auto" | "manual" | "confirmed";
  network_id: number | null;
  network_scope: NetworkScope;
}

export interface CommissioningEquipmentItem {
  id: number;
  job_room_id: number | null;
  description: string;
  model_no: string | null;
  manufacturer_name: string | null;
  progress_percent: number;
  not_required: boolean;
  assigned_engineer_id: string | null;
  assigned_engineer_name: string | null;
  notes: string | null;
  is_mtr_device: boolean;
  mtr_updated: boolean;
  firmware_updated: boolean;
  needs_control_ids: boolean;
  control_ids: Record<string, string | null>;
  serials: { id: number; serial_number: string }[];
  network_interfaces: CommissioningNetworkInterfaceSummary[];
  photo_count: number;
  qty_required: number;
  qty_received: number;
  equipment_role: EquipmentRole | null;
  last_activity_at: string | null;
}

export interface CommissioningRoomGroup {
  job_room_id: number;
  name: string;
  progress_percent: number;
  total_items: number;
  equipment: CommissioningEquipmentItem[];
}

export interface CommissioningSharedGroup {
  progress_percent: number;
  total_items: number;
  equipment: CommissioningEquipmentItem[];
}

export interface CommissioningActivityEntry {
  id: number;
  event_type: string;
  summary: string;
  detail: Record<string, unknown>;
  actor_name: string | null;
  created_at: string;
  equipment_id: number | null;
  equipment_description: string | null;
}

export interface CommissioningNetworkProfileSummary {
  id: number;
  name: string;
  purpose: string;
  subnet_cidr: string;
  gateway: string | null;
  dns: string | null;
  dhcp_policy: string | null;
  is_default: boolean;
  notes: string | null;
}

/** The whole project commissioning workspace in one round trip (0157) --
 * summary + room groups + shared infrastructure + network profiles +
 * recent activity. Avoids firing a separate request per equipment card. */
export interface CommissioningWorkspace {
  job_id: number;
  total_items: number;
  applicable_items: number;
  not_required_items: number;
  progress_percent: number;
  complete_items: number;
  in_progress_items: number;
  not_started_items: number;
  checked_in_items: number;
  rooms: CommissioningRoomGroup[];
  shared: CommissioningSharedGroup | null;
  networks: CommissioningNetworkProfileSummary[];
  recent_activity: CommissioningActivityEntry[];
  last_activity_at: string | null;
}

export async function fetchCommissioningWorkspace(jobId: number): Promise<CommissioningWorkspace> {
  const { data, error } = await supabase.rpc("job_commissioning_workspace", { p_job_id: jobId });
  if (error) throw error;
  return data as CommissioningWorkspace;
}

/** Deterministic IP suggestions for every network-capable item on the
 * job (0157) -- auto-creates a provisional network the first time it's
 * needed, never touches a manually-adjusted or confirmed address. Safe
 * to re-run after adding equipment. */
export async function generateNetworkSuggestions(jobId: number): Promise<{ network_id: number; subnet_cidr: string; assigned: number }> {
  const { data, error } = await supabase.rpc("job_network_generate_suggestions", { p_job_id: jobId });
  if (error) throw error;
  return data as { network_id: number; subnet_cidr: string; assigned: number };
}

/** Copies a suggested address into the actual/confirmed field -- the
 * engineer can still edit it immediately after accepting. */
export async function acceptSuggestedIp(interfaceId: number): Promise<void> {
  const { error } = await supabase.rpc("job_network_accept_suggestion", { p_interface_id: interfaceId });
  if (error) throw error;
}

export async function addCommissioningHandoverNote(jobId: number, note: string): Promise<void> {
  const { error } = await supabase.rpc("job_commissioning_add_handover_note", { p_job_id: jobId, p_note: note });
  if (error) throw error;
}

/** Opt-in re-check against the corrected role/capability-aware eligibility
 * logic (0155) -- never touches an item an engineer has already started or
 * finished. */
export async function recomputeCommissioningEligibility(jobId: number): Promise<{ changed: number }> {
  const { data, error } = await supabase.rpc("job_recompute_commissioning_eligibility", { p_job_id: jobId });
  if (error) throw error;
  return data as { changed: number };
}

/** Flips commissioning_status not_started -> in_progress and seeds the
 * checklist from the linked product's default_checklist, once (0152) --
 * an RPC, not a plain write, since it also does the seed-if-empty check
 * server-side rather than racing a client-side "have I seeded yet?". */
export async function startEquipmentCommissioning(equipmentId: number): Promise<{ commissioning_status: CommissioningStatus; started_at: string }> {
  const { data, error } = await supabase.rpc("job_equipment_commissioning_start", { p_equipment_id: equipmentId });
  if (error) throw error;
  return data as { commissioning_status: CommissioningStatus; started_at: string };
}

export async function setAssignedEngineer(equipmentId: number, profileId: string | null): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ assigned_engineer_id: profileId }).eq("id", equipmentId);
  if (error) throw error;
}

export async function updateCommissioningNotes(equipmentId: number, notes: string | null): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ commissioning_notes: notes }).eq("id", equipmentId);
  if (error) throw error;
}

export interface CommissioningCheck {
  id: number;
  equipment_id: number;
  label: string;
  is_complete: boolean;
  completed_by: string | null;
  completed_at: string | null;
  sort_order: number;
}

export async function fetchCommissioningChecks(equipmentId: number): Promise<CommissioningCheck[]> {
  const { data, error } = await supabase
    .from("job_equipment_commissioning_checks")
    .select("id, equipment_id, label, is_complete, completed_by, completed_at, sort_order")
    .eq("equipment_id", equipmentId)
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as CommissioningCheck[];
}

export async function setCommissioningCheckComplete(id: number, isComplete: boolean, byUserId: string | null): Promise<void> {
  const { error } = await supabase
    .from("job_equipment_commissioning_checks")
    .update(
      isComplete
        ? { is_complete: true, completed_by: byUserId, completed_at: new Date().toISOString() }
        : { is_complete: false, completed_by: null, completed_at: null },
    )
    .eq("id", id);
  if (error) throw error;
}

export interface CommissioningPhoto {
  id: number;
  equipment_id: number;
  file_id: number;
  caption: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  storage_path: string;
}

export async function fetchCommissioningPhotos(equipmentId: number): Promise<CommissioningPhoto[]> {
  const { data, error } = await supabase
    .from("job_equipment_commissioning_photos")
    .select("id, equipment_id, file_id, caption, uploaded_by, uploaded_at, files(storage_path)")
    .eq("equipment_id", equipmentId)
    .order("uploaded_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as CommissioningPhoto & { files: { storage_path: string } | null };
    return { ...row, storage_path: row.files?.storage_path ?? "" };
  });
}

/** Reuses the existing job-photos bucket (0011) -- no new storage concept
 * for commissioning evidence, same generic files-table pattern RAMS uses. */
export async function uploadCommissioningPhoto(
  equipmentId: number,
  file: File,
  caption: string | null,
  uploadedBy: string,
): Promise<void> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `commissioning/${equipmentId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage.from("job-photos").upload(path, file, { contentType: file.type || "image/jpeg" });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({ bucket_id: "job-photos", storage_path: path, mime_type: file.type || "image/jpeg", size_bytes: file.size, uploaded_by: uploadedBy })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { error } = await supabase
    .from("job_equipment_commissioning_photos")
    .insert({ equipment_id: equipmentId, file_id: fileRow.id, caption, uploaded_by: uploadedBy });
  if (error) throw error;
}

export async function deleteCommissioningPhoto(id: number): Promise<void> {
  const { error } = await supabase.from("job_equipment_commissioning_photos").delete().eq("id", id);
  if (error) throw error;
}

export async function commissioningPhotoUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage.from("job-photos").createSignedUrl(storagePath, 300);
  if (error) throw error;
  return data.signedUrl;
}

/* --------------------------------------------------- Report a product issue */
// Something wrong with a specific piece of equipment's Product Library
// data, flagged from the Rack/Locations/Connections tabs -- not a research
// pass on a new SKU, a correction to one already on the room's own
// equipment list. Lands as a pending revision on the same Product Library
// Revisions tab (0100/0462), reviewed the same way, never applied
// automatically.

/** DOCUMENT_PROFILE, not EVIDENCE_PROFILE -- this is a photo of a
 * nameplate/rating label whose small print has to be read (by Claude,
 * then by whoever reviews the revision), the same reasoning a garage
 * health report's photo gets the higher-resolution profile. Reuses the
 * job-photos bucket (0011/0045, can_manage_wip write policy already
 * matches this feature's own gating) under its own path, same pattern as
 * uploadCommissioningPhoto's commissioning/{equipmentId}/... convention. */
export async function uploadProductIssuePhoto(
  equipmentId: number,
  file: File,
  uploadedBy: string,
): Promise<number> {
  const { blob, mimeType } = await compressImage(file, DOCUMENT_PROFILE);
  const ext = mimeType === "image/jpeg" ? "jpg" : (mimeType.split("/")[1] ?? "bin");
  const path = `product-report/${equipmentId}/${Date.now()}-${Math.round(Math.random() * 1e6)}.${ext}`;
  const { error: upErr } = await supabase.storage.from("job-photos").upload(path, blob, { contentType: mimeType });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from("files")
    .insert({ bucket_id: "job-photos", storage_path: path, mime_type: mimeType, size_bytes: blob.size, uploaded_by: uploadedBy })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

const REPORT_ISSUE_POLL_MS = 1500;
const REPORT_ISSUE_POLL_LIMIT = 30; // ~45s -- one small structured-JSON completion, same budget as AI research.

export interface ReportProductIssueResult {
  ok: boolean;
  revision_id?: number;
  drafted?: boolean;
  reason?: string | null;
  error?: string;
}

/** Same transport as requestAiResearch: managed devices on this fleet
 * block a direct browser POST to /functions/v1, so this always goes
 * through app.request_manual_sync's pg_net relay + a manual_sync_result
 * poll. Gated on can_manage_wip (0462), not can_manage_product_intelligence
 * -- filing a report is what this permission already covers throughout
 * these tabs. */
export async function reportProductIssue(input: {
  jobEquipmentId: number;
  description: string;
  evidenceFileId?: number | null;
}): Promise<ReportProductIssueResult> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "report-product-issue",
    p_body: {
      job_equipment_id: input.jobEquipmentId,
      description: input.description,
      evidence_file_id: input.evidenceFileId ?? null,
    },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: ReportProductIssueResult } = { done: false };
  for (let i = 0; i < REPORT_ISSUE_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, REPORT_ISSUE_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("This is taking longer than expected -- check back shortly.");
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(outcome.body?.error ?? `Reporting the issue failed (${outcome.status}).`);
  }
  return outcome.body ?? { ok: false, error: "No response received" };
}

export async function updateControlIds(equipmentId: number, controlIds: Record<string, string | null>): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ control_ids: controlIds }).eq("id", equipmentId);
  if (error) throw error;
}

export interface NetworkInterfaceInput {
  label: string;
  ip_address: string | null;
  mac_address: string | null;
  hostname: string | null;
  network_scope: NetworkScope;
  notes: string | null;
  assignment_mode?: "static" | "dhcp" | "reserved";
}

/** An engineer typing their own actual IP is, by definition, confirming
 * it (0157) -- distinct from a system-generated suggestion still open to
 * regeneration. Clearing the IP (e.g. switching to DHCP) reverts to
 * manual so a later suggestion pass can propose one again if needed. */
export async function addNetworkInterface(equipmentId: number, input: NetworkInterfaceInput, sortOrder: number): Promise<void> {
  const ip_source = input.ip_address ? "confirmed" : "manual";
  const { error } = await supabase.from("job_equipment_network_interfaces").insert({ equipment_id: equipmentId, sort_order: sortOrder, ip_source, ...input });
  if (error) throw error;
}

export async function updateNetworkInterface(id: number, input: NetworkInterfaceInput): Promise<void> {
  const ip_source = input.ip_address ? "confirmed" : "manual";
  const { error } = await supabase.from("job_equipment_network_interfaces").update({ ip_source, ...input }).eq("id", id);
  if (error) throw error;
}

export async function deleteNetworkInterface(id: number): Promise<void> {
  const { error } = await supabase.from("job_equipment_network_interfaces").delete().eq("id", id);
  if (error) throw error;
}

export interface NetgearPoeGuide {
  poe_budget_watts: number | null;
  poe_rj45_ports: number;
  total_rj45_ports: number;
}

/** The "quick floating guide" for a Netgear (or any PoE switch) item at
 * commissioning: total PoE budget wattage and how many of its RJ45 ports
 * are actually PoE-capable, read straight from the Product Library. */
export async function fetchPoeGuide(profileId: number): Promise<NetgearPoeGuide> {
  const [{ data: profile, error: pErr }, { data: ports, error: portErr }] = await Promise.all([
    supabase.from("av_product_profiles").select("poe_budget_watts").eq("id", profileId).maybeSingle(),
    supabase.from("av_product_ports").select("is_poe, connector_type:av_connector_types(key)").eq("profile_id", profileId),
  ]);
  if (pErr) throw pErr;
  if (portErr) throw portErr;
  const rj45Ports = (ports ?? []).filter((p) => (p.connector_type as unknown as { key: string } | null)?.key === "rj45");
  return {
    poe_budget_watts: profile?.poe_budget_watts ?? null,
    poe_rj45_ports: rj45Ports.filter((p) => p.is_poe).length,
    total_rj45_ports: rj45Ports.length,
  };
}

/* ---------------------------------------------------- AV network IP autogen */

export interface NetworkAutogenResult {
  assigned_ips: number;
  assigned_control_ids: number;
  unassigned: { network_interface_id: number; reason: string }[];
  plan: { role_key: string; block_start_ip: string; block_end_ip: string }[];
}

/** Fills in blank ip_address values on Viewpoint-AV-network NICs (by
 * av_product_profiles.network_role, in av_ip_plan_blocks order) and blank
 * control_ids values (IP ID / Cresnet ID etc., one sequence per label).
 * Additive and idempotent -- never overwrites a value already set. */
export async function autogenerateJobNetwork(jobId: number): Promise<NetworkAutogenResult> {
  const { data, error } = await supabase.rpc("job_network_autogenerate", { p_job_id: jobId });
  if (error) throw error;
  return data as NetworkAutogenResult;
}

export interface DhcpMacHistoryEntry {
  old_mac: string | null;
  changed_at: string;
  changed_by: string | null;
}

export interface DhcpReservation {
  id: number;
  job_id: number;
  equipment_id: number | null;
  network_interface_id: number | null;
  switch_profile_id: number | null;
  hostname: string | null;
  ip_address: string;
  mac_address: string;
  mac_history: DhcpMacHistoryEntry[];
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchDhcpReservations(jobId: number): Promise<DhcpReservation[]> {
  const { data, error } = await supabase
    .from("job_dhcp_reservations")
    .select("id, job_id, equipment_id, network_interface_id, switch_profile_id, hostname, ip_address, mac_address, mac_history, notes, created_at, updated_at")
    .eq("job_id", jobId)
    .order("ip_address");
  if (error) throw error;
  return (data ?? []).map((r) => ({ ...r, mac_history: (r.mac_history ?? []) as DhcpMacHistoryEntry[] })) as DhcpReservation[];
}

export interface DhcpReservationInput {
  hostname: string | null;
  ip_address: string;
  mac_address: string;
  notes: string | null;
  switch_profile_id: number | null;
}

export async function addDhcpReservation(jobId: number, input: DhcpReservationInput, byUserId: string | null): Promise<void> {
  const { error } = await supabase.from("job_dhcp_reservations").insert({ job_id: jobId, created_by: byUserId, ...input });
  if (error) throw error;
}

/** Updating mac_address here is exactly the "swap out kit, keep the
 * reservation" flow -- the job_dhcp_reservations_track_mac trigger appends
 * the old value to mac_history automatically. */
export async function updateDhcpReservation(id: number, input: DhcpReservationInput): Promise<void> {
  const { error } = await supabase.from("job_dhcp_reservations").update(input).eq("id", id);
  if (error) throw error;
}

export async function deleteDhcpReservation(id: number): Promise<void> {
  const { error } = await supabase.from("job_dhcp_reservations").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------ commissioning defaults */

/** The standing default management profile for a fresh-out-of-the-box
 * Netgear switch -- reference data only, never pushed to a device. A
 * global setting (app_settings key "wip.netgear_default_management"),
 * not per Product Library model, per scope decision. */
export interface NetgearDefaultManagement {
  dhcp_enabled: boolean;
  default_ip: string;
  subnet_mask: string;
}

const NETGEAR_SETTING_KEY = "wip.netgear_default_management";

export async function fetchNetgearDefaultManagement(): Promise<NetgearDefaultManagement | null> {
  const { data, error } = await supabase.from("app_settings").select("value").eq("key", NETGEAR_SETTING_KEY).maybeSingle();
  if (error) throw error;
  return (data?.value as NetgearDefaultManagement | undefined) ?? null;
}

export async function updateNetgearDefaultManagement(value: NetgearDefaultManagement): Promise<void> {
  const { error } = await supabase.rpc("wip_update_setting", { p_key: NETGEAR_SETTING_KEY, p_value: value });
  if (error) throw error;
}

/** Standard/factory-default login credentials for a kit type (e.g. a
 * Crestron control processor's default admin login) -- not job-specific,
 * see job_credentials for that. Vault-backed exactly the same way:
 * has_secret is derived, the value itself is only ever fetched via the
 * explicit reveal RPC. A global reference list, not filtered per job. */
export interface DefaultCredential {
  id: number;
  manufacturer_id: number | null;
  manufacturer_name: string | null;
  category: string | null;
  label: string;
  username: string | null;
  has_secret: boolean;
  notes: string | null;
  created_at: string;
}

export async function fetchDefaultCredentials(): Promise<DefaultCredential[]> {
  const { data, error } = await supabase
    .from("av_default_credentials")
    .select("id, manufacturer_id, category, label, username, vault_secret_id, notes, created_at, manufacturers(name)")
    .order("label");
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    manufacturer_id: r.manufacturer_id,
    manufacturer_name: (r.manufacturers as unknown as { name: string } | null)?.name ?? null,
    category: r.category,
    label: r.label,
    username: r.username,
    has_secret: r.vault_secret_id != null,
    notes: r.notes,
    created_at: r.created_at,
  }));
}

export interface DefaultCredentialInput {
  manufacturer_id: number | null;
  category: string | null;
  label: string;
  username: string | null;
  secret: string | null;
  notes: string | null;
}

export async function createDefaultCredential(input: DefaultCredentialInput): Promise<number> {
  const { data, error } = await supabase.rpc("av_default_credential_create", {
    p_manufacturer_id: input.manufacturer_id,
    p_category: input.category,
    p_label: input.label,
    p_username: input.username,
    p_secret: input.secret,
    p_notes: input.notes,
  });
  if (error) throw error;
  return data as number;
}

export async function revealDefaultCredentialSecret(id: number): Promise<string | null> {
  const { data, error } = await supabase.rpc("av_default_credential_reveal", { p_id: id });
  if (error) throw error;
  return data as string | null;
}

export async function updateDefaultCredentialSecret(id: number, secret: string): Promise<void> {
  const { error } = await supabase.rpc("av_default_credential_update_secret", { p_id: id, p_secret: secret });
  if (error) throw error;
}

export async function deleteDefaultCredential(id: number): Promise<void> {
  const { error } = await supabase.rpc("av_default_credential_delete", { p_id: id });
  if (error) throw error;
}

/** Marks a requirement line ordered in one shot (the full remaining
 * quantity, not a partial-order picker — matches the "checkbox + PO
 * number" step in the real ordering workflow, not a purchasing system).
 * Passing null for poNumber/orderDate un-orders the line instead. */
export async function markEquipmentOrdered(
  requirement: Pick<EquipmentRequirement, "id" | "qty_required">,
  poNumber: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("job_equipment_requirements")
    .update(
      poNumber
        ? { qty_ordered: requirement.qty_required, order_reference: poNumber, order_date: new Date().toISOString().slice(0, 10) }
        : { qty_ordered: 0, order_reference: null, order_date: null },
    )
    .eq("id", requirement.id);
  if (error) throw error;
}

/** Records one received unit against a requirement line — a serial (and
 * optional MAC) — and lands the physical unit in warehouse stock as
 * 'allocated' (reserved for this job) in the same call. An RPC, not a
 * plain client write, because job_equipment_serials and stock_items sit
 * behind different permissions (can_manage_wip vs can_manage_stock) and
 * receiving equipment on a job shouldn't require a separate stock grant. */
export async function receiveEquipmentSerial(
  equipmentId: number,
  serialNumber: string,
  macAddress: string | null,
): Promise<{ qty_received: number; stock_item_id: number }> {
  const { data, error } = await supabase.rpc("receive_job_equipment_serial", {
    p_equipment_id: equipmentId,
    p_serial_number: serialNumber,
    p_mac_address: macAddress,
  });
  if (error) throw error;
  return data as { qty_received: number; stock_item_id: number };
}

/** The "until job signed off" trigger: marks the job complete, stamps a
 * job_signoffs header row, and releases every stock item this job pulled
 * into the warehouse via receipt out of 'allocated' tracking. Also an
 * RPC for the same permission-crossing reason as receiveEquipmentSerial. */
export async function markJobComplete(jobId: number): Promise<{ already_complete: boolean; released: number }> {
  const { data, error } = await supabase.rpc("job_mark_complete", { p_job_id: jobId });
  if (error) throw error;
  return data as { already_complete: boolean; released: number };
}

/** Global lookup for "scan any serial, jump to its job" — not scoped to
 * a job already open. Returns null if nothing recorded that serial. */
export async function findJobBySerial(
  serial: string,
): Promise<{ job_id: number; job_name: string; project_reference: string; requirement_id: number; description: string } | null> {
  const { data, error } = await supabase.rpc("find_job_by_serial", { p_serial: serial });
  if (error) throw error;
  const row = (data ?? [])[0];
  return row ?? null;
}

/* Job credentials are no longer part of Projects.
 *
 * The tab that revealed them, and the client-side calls behind it, are
 * gone. job_credentials, its RPCs and can_manage_job_credentials are
 * untouched on the server: this removes the way in from here, it does
 * not throw the record away. */

/* ---------------------------------------------------------- work stages */

export interface WorkStage {
  id: number;
  key: string;
  label: string;
  colour: string;
  sort_order: number;
}

export async function fetchWorkStages(): Promise<WorkStage[]> {
  const { data, error } = await supabase
    .from("work_stages")
    .select("id, key, label, colour, sort_order")
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as WorkStage[];
}

/* ------------------------------------------------------------- planner */

export interface PlannerJob {
  id: number;
  name: string;
  project_reference: string;
  job_number: string | null;
  status: string;
  company_id: number;
  company_name: string | null;
  pm_id: string | null;
  pm_name: string | null;
  first_fix_days: number;
  second_fix_days: number;
  commissioning_days: number;
  other_days: number;
  /** Null unless the caller holds can_view_project_commercials. */
  project_value: number | null;
  start_date: string | null;
  target_completion: string | null;
}

export async function fetchPlannerJobs(): Promise<PlannerJob[]> {
  const { data, error } = await supabase.from("v_planner_jobs").select("*").order("name");
  if (error) throw error;
  return (data ?? []) as PlannerJob[];
}

export interface PlannerPerson {
  id: string;
  full_name: string;
  planning_colour: string | null;
  engineer_type: string | null;
  employment_type: string | null;
}

export async function fetchPlannerPeople(): Promise<PlannerPerson[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, planning_colour, engineer_type, employment_type")
    .eq("is_active", true)
    .order("full_name");
  if (error) throw error;
  return (data ?? []) as PlannerPerson[];
}

export interface Assignment {
  id: number;
  job_id: number;
  profile_id: string;
  work_stage_id: number;
  assignment_date: string;
  notes: string | null;
}

/** Every day-row in [startDate, endDate] inclusive — the Planner does its
 * own client-side grouping into visual "booking" segments, matching the
 * real Glide behaviour (docs/wip-migration-audit.md §3/§5). */
export async function fetchAssignments(startDate: string, endDate: string): Promise<Assignment[]> {
  const { data, error } = await supabase
    .from("job_assignments")
    .select("id, job_id, profile_id, work_stage_id, assignment_date, notes")
    .gte("assignment_date", startDate)
    .lte("assignment_date", endDate);
  if (error) throw error;
  return (data ?? []) as Assignment[];
}

export async function bulkCreateAssignments(
  jobId: number,
  profileId: string,
  workStageId: number,
  dates: string[],
  notes: string | null,
): Promise<Assignment[]> {
  const { data, error } = await supabase.rpc("job_assignment_bulk_create", {
    p_job_id: jobId,
    p_profile_id: profileId,
    p_work_stage_id: workStageId,
    p_dates: dates,
    p_notes: notes,
  });
  if (error) throw error;
  return (data ?? []) as Assignment[];
}

export async function bulkDeleteAssignments(ids: number[]): Promise<void> {
  const { error } = await supabase.rpc("job_assignment_bulk_delete", { p_ids: ids });
  if (error) throw error;
}

export interface HolidayRow {
  id: number;
  profile_id: string;
  holiday_date: string;
  day_fraction: number;
  status: "pending" | "approved" | "declined";
  notes: string | null;
}

/** Every staff holiday (any status) in range — used both by the Planner's
 * non-blocking clash warning (docs/wip-migration-audit.md §5/§9 decision
 * 2) and by My Schedule. holidays' own RLS (can_view_wip) already permits
 * this broadly; the real request/approve authorisation lives in the
 * holiday_request_create/holiday_decide RPCs, not here. */
export async function fetchHolidaysInRange(startDate: string, endDate: string): Promise<HolidayRow[]> {
  const { data, error } = await supabase
    .from("holidays")
    .select("id, profile_id, holiday_date, day_fraction, status, notes")
    .gte("holiday_date", startDate)
    .lte("holiday_date", endDate);
  if (error) throw error;
  return (data ?? []) as HolidayRow[];
}

export interface PublicOrCompanyHoliday {
  holiday_date: string;
  name: string;
}

export async function fetchNonWorkingDays(startDate: string, endDate: string): Promise<PublicOrCompanyHoliday[]> {
  const [pub, co] = await Promise.all([
    supabase.from("public_holidays").select("holiday_date, name").gte("holiday_date", startDate).lte("holiday_date", endDate),
    supabase.from("company_holidays").select("holiday_date, name").gte("holiday_date", startDate).lte("holiday_date", endDate),
  ]);
  if (pub.error) throw pub.error;
  if (co.error) throw co.error;
  return [...(pub.data ?? []), ...(co.data ?? [])] as PublicOrCompanyHoliday[];
}

/* ---------------------------------------------------------- my schedule */

/** The engineer's Service visits (PM + reactive) from the unified schedule
 * view, so My Schedule shows service work alongside WIP job bookings. */
export async function fetchMyServiceVisits(profileId: string, startDate: string, endDate: string) {
  const { data, error } = await supabase
    .from("v_engineer_schedule")
    .select("source_id, event_date, title, visit_type, status, link, site_name")
    .eq("engineer_id", profileId)
    .eq("source_type", "service_visit")
    .gte("event_date", startDate)
    .lte("event_date", endDate)
    .order("event_date");
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    source_id: number;
    event_date: string;
    title: string;
    visit_type: string | null;
    status: string | null;
    link: string;
    site_name: string | null;
  }>;
}

export async function fetchMyAssignments(profileId: string, startDate: string, endDate: string) {
  const { data, error } = await supabase
    .from("job_assignments")
    .select("id, job_id, work_stage_id, assignment_date, notes, jobs(name, project_reference), work_stages(label, colour)")
    .eq("profile_id", profileId)
    .gte("assignment_date", startDate)
    .lte("assignment_date", endDate)
    .order("assignment_date");
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    id: number;
    job_id: number;
    work_stage_id: number;
    assignment_date: string;
    notes: string | null;
    jobs: { name: string; project_reference: string } | null;
    work_stages: { label: string; colour: string } | null;
  }>;
}

export async function fetchMyHolidays(profileId: string): Promise<HolidayRow[]> {
  const { data, error } = await supabase
    .from("holidays")
    .select("id, profile_id, holiday_date, day_fraction, status, notes")
    .eq("profile_id", profileId)
    .order("holiday_date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as HolidayRow[];
}

export async function requestHoliday(holidayDate: string, dayFraction: number, notes: string | null): Promise<number> {
  const { data, error } = await supabase.rpc("holiday_request_create", {
    p_holiday_date: holidayDate,
    p_day_fraction: dayFraction,
    p_notes: notes,
  });
  if (error) throw error;
  return data as number;
}

export interface PendingHolidayApproval extends HolidayRow {
  profile_name: string;
}

/** Everyone with can_authorise_holidays sees every pending request here —
 * holidays' own RLS is a broad backstop (docs/wip-migration-audit.md §9
 * comment on the table); holiday_decide() itself is what actually
 * enforces the director/engineer_type scoping when a decision is made,
 * so an out-of-scope request simply fails there with a clear error
 * rather than being hidden here (matches this app's existing convention
 * of never silently filtering what the RPC itself is the real gate for). */
export async function fetchPendingHolidayApprovals(): Promise<PendingHolidayApproval[]> {
  const { data, error } = await supabase
    .from("holidays")
    .select("id, profile_id, holiday_date, day_fraction, status, notes, profiles(full_name)")
    .eq("status", "pending")
    .order("holiday_date");
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as HolidayRow & { profiles: { full_name: string } | null };
    return { ...row, profile_name: row.profiles?.full_name ?? "Unknown" };
  });
}

export async function decideHoliday(id: number, decision: "approved" | "declined", notes: string | null): Promise<void> {
  const { error } = await supabase.rpc("holiday_decide", { p_holiday_id: id, p_decision: decision, p_notes: notes });
  if (error) throw error;
}

/* ------------------------------------------------------------------ rams */

export interface RamsDocument {
  id: number;
  job_id: number;
  title: string;
  reference: string | null;
  version: number;
  file_id: number | null;
  status: string;
  created_at: string;
  storage_path?: string | null;
}

export async function fetchJobRamsDocuments(jobId: number): Promise<RamsDocument[]> {
  const { data, error } = await supabase
    .from("rams_documents")
    .select("id, job_id, title, reference, version, file_id, status, created_at, files(storage_path)")
    .eq("job_id", jobId)
    .order("version", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as RamsDocument & { files: { storage_path: string } | null };
    return { ...row, storage_path: row.files?.storage_path ?? null };
  });
}

/** Computes the file's SHA-256 client-side (Web Crypto) so `files.checksum_sha256`
 * is populated at upload time — nothing else in this codebase does yet,
 * but app.rams_sign() trusts this column, never a client-supplied hash. */
async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function uploadRamsDocument(
  jobId: number,
  title: string,
  reference: string | null,
  file: File,
  uploadedBy: string,
): Promise<number> {
  const hash = await sha256Hex(file);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `rams/${jobId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage.from("documents").upload(path, file, {
    contentType: file.type || "application/pdf",
  });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({
      bucket_id: "documents",
      storage_path: path,
      mime_type: file.type || "application/pdf",
      size_bytes: file.size,
      checksum_sha256: hash,
      uploaded_by: uploadedBy,
    })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { data: existing, error: verErr } = await supabase
    .from("rams_documents")
    .select("version")
    .eq("job_id", jobId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (verErr) throw verErr;
  const nextVersion = (existing?.version ?? 0) + 1;

  const { data: doc, error: docErr } = await supabase
    .from("rams_documents")
    .insert({
      job_id: jobId,
      title,
      reference,
      version: nextVersion,
      file_id: fileRow.id,
      status: "approved",
      created_by: uploadedBy,
    })
    .select("id")
    .single();
  if (docErr) throw docErr;

  const { error: jobErr } = await supabase.from("jobs").update({ active_rams_document_id: doc.id }).eq("id", jobId);
  if (jobErr) throw jobErr;

  return doc.id as number;
}

/** Uploads the drawn signature PNG, then records it via app.rams_sign()
 * — the RPC re-reads the document's checksum server-side rather than
 * trusting anything from this call, per docs/wip-migration-audit.md §17-18. */
export async function signRamsDocument(
  jobId: number,
  ramsDocumentId: number,
  signaturePngBlob: Blob,
  signedBy: string,
): Promise<number> {
  const path = `rams-signatures/${jobId}/${Date.now()}-${signedBy}.png`;
  const { error: upErr } = await supabase.storage.from("documents").upload(path, signaturePngBlob, { contentType: "image/png" });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({
      bucket_id: "documents",
      storage_path: path,
      mime_type: "image/png",
      size_bytes: signaturePngBlob.size,
      uploaded_by: signedBy,
    })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { data, error } = await supabase.rpc("rams_sign", {
    p_job_id: jobId,
    p_rams_document_id: ramsDocumentId,
    p_signature_file_id: fileRow.id,
  });
  if (error) throw error;
  return data as number;
}

export interface RamsSignature {
  id: number;
  profile_id: string;
  profile_name: string;
  revision: number;
  signed_at: string;
}

export async function fetchRamsSignatures(jobId: number): Promise<RamsSignature[]> {
  const { data, error } = await supabase
    .from("rams_signatures")
    .select("id, profile_id, revision, signed_at, profiles(full_name)")
    .eq("job_id", jobId)
    .order("signed_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as RamsSignature & { profiles: { full_name: string } | null };
    return { ...row, profile_name: row.profiles?.full_name ?? "Unknown" };
  });
}

export interface MyRamsOutstanding {
  job_id: number;
  job_name: string;
  project_reference: string;
  rams_document_id: number;
  version: number;
  file_id: number | null;
  title: string;
  storage_path?: string | null;
}

export async function signedDocumentUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage.from("documents").createSignedUrl(storagePath, 300);
  if (error) throw error;
  return data.signedUrl;
}

export async function fetchMyRamsOutstanding(): Promise<MyRamsOutstanding[]> {
  const { data, error } = await supabase.from("v_my_rams_outstanding").select("*, files(storage_path)");
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as MyRamsOutstanding & { files: { storage_path: string } | null };
    return { ...row, storage_path: row.files?.storage_path ?? null };
  });
}

/* ------------------------------------------------------------- project analysis */

/** Live-project profitability (quoted, frozen at Won, vs. actual from the
 * ledger) plus PM/office-admin attention counts. Money columns come back
 * null when the caller lacks can_view_project_commercials/can_view_finance
 * -- masked server-side, same convention as the Planner's project_value. */
export interface ProjectAnalysisRow {
  job_id: number;
  project_reference: string | null;
  job_name: string;
  company_name: string;
  pm_name: string | null;
  status: string;
  target_completion: string | null;
  quoted_sell: number | null;
  quoted_cost: number | null;
  quoted_margin_pct: number | null;
  actual_revenue: number | null;
  actual_cost: number | null;
  actual_margin: number | null;
  actual_margin_pct: number | null;
  equipment_outstanding_count: number;
  equipment_held_count: number;
  commissioning_outstanding_count: number;
}

export async function fetchProjectAnalysis(): Promise<ProjectAnalysisRow[]> {
  const { data, error } = await supabase.rpc("project_analysis_list");
  if (error) throw error;
  return (data ?? []) as ProjectAnalysisRow[];
}

/* ------------------------------------------------------------- rack builder */

/** The real Rack Builder, Project stage (0141) -- quote stage (Quotes ->
 * System Planning) only ever checks rack-U capacity; this is where a rack
 * actually gets built: which quoted rack, which equipment goes in it, in
 * what order top-to-bottom. Mirrors quote_racks/quote_rack_items (0113,
 * left unused after the quote-stage simplification) keyed off
 * job_equipment_requirements instead of quote_line_items. */
export type JobRackType = "open_frame" | "floor_cabinet" | "wall_cabinet" | "existing_client_rack";
export type JobRackAirConditioned = "yes" | "no" | "unknown";

export interface JobRack {
  id: number;
  job_id: number;
  room_label: string | null;
  job_room_id: number | null;
  label: string;
  rack_type: JobRackType;
  /** Null for an existing_client_rack with no known capacity (0185) --
   * meaningless otherwise, always set for a Viewpoint-supplied rack. */
  total_u: number | null;
  has_fan_tray: boolean;
  has_active_ventilation: boolean;
  room_air_conditioned: JobRackAirConditioned;
  /** Superseded by job_rack_pdus (0185) -- kept on the row as a frozen
   * historical value, no longer written by new Add/Edit rack UI. */
  pdu_count: number;
  pdu_outlets_per_unit: number;
  environment_notes: string | null;
  sort_order: number;
  created_at: string;
  /** Where the rack is, since 0232: one of these, never both. The
   * quoted room and room_label are derived from whichever is set. */
  space_id: number | null;
  equipment_store_id: number | null;
  /** Where it SITS -- separate from rack_type, which is what the
   * enclosure IS. A wall cabinet can sit on a credenza shelf. */
  installation_type: string | null;
  custom_installation_text: string | null;
  spare_capacity_pct: number;
  credenza_limit_u: number;
  /** existing_client_rack only (0185). Both null = entirely unknown. */
  client_rack_known_occupied_u: number | null;
  client_rack_known_available_u: number | null;
}

export type JobRackItemMountMethod =
  | "native_rackmount"
  | "dedicated_shelf"
  | "shared_shelf"
  | "rear_mounted"
  | "zero_u"
  | "loose"
  | "other";

export interface JobRackItem {
  id: number;
  rack_id: number;
  job_equipment_id: number;
  sort_order: number;
  actual_sort_order: number | null;
  notes: string | null;
  mount_method: JobRackItemMountMethod;
  shelf_id: number | null;
  dedicated_shelf_u: number | null;
  /** Null: packed, position comes from sort_order as always. A number:
   * pinned at that U offset from the top of the elevation, out of the
   * packed flow (0449). */
  start_u: number | null;
}

/** A named place inside a rack that several devices share one U-cost for
 * -- "Shelf 1", "Shelf 2". Schema since 0232 (job_rack_shelves); this is
 * its first write path (0429). */
export interface JobRackShelf {
  id: number;
  rack_id: number;
  name: string;
  shelf_u: number;
  /** Same numbering space as job_rack_items.sort_order (0448) -- merge
   * and sort both together for the elevation's actual draggable order. */
  sort_order: number;
  /** Null: packed, position comes from sort_order as always. A number:
   * pinned at that U offset from the top of the elevation, out of the
   * packed flow (0450, mirrors job_rack_items.start_u from 0449). */
  start_u: number | null;
  /** Set once someone links this shelf to a real Product Library product
   * (0453) -- the job_equipment_requirements row that makes it visible to
   * Kit Balance/the Equipment tab, neither of which reads this table. */
  job_equipment_id: number | null;
}

export interface JobRackCheckResult {
  rack_id: number;
  room_label: string | null;
  job_room_id: number | null;
  label: string;
  rack_type: JobRackType;
  /** Effective capacity: total_u for a Viewpoint rack, occupied+available
   * for an existing_client_rack when both are known, else null. */
  total_u: number | null;
  /** Total vertical space lost: equipment_u + shelf_u + pdu_u, from
   * app.rack_u_breakdown. A mounting's cost, not a box's height -- a
   * desktop switch on a 1U shelf is 1U, and behind a display it is 0U. */
  u_consumed: number;
  /** What that total is made of. */
  equipment_u: number;
  shelf_u: number;
  pdu_u: number;
  /** False when some item's height has never been recorded, in which case
   * u_consumed is a floor and free_u is optimistic. */
  u_is_complete: boolean;
  /** The products whose height nobody has stated, named. */
  missing_rack_u: string[];
  u_unknown_count: number;
  /** Rear-mounted, zero-U and loose kit: in the rack, out of the front total. */
  off_front_count: number;
  /** Null when an existing_client_rack has no known available capacity. */
  free_u: number | null;
  client_rack_known_occupied_u: number | null;
  client_rack_known_available_u: number | null;
  weight_known_kg: number;
  weight_pct_in_lower_third: number | null;
  outlets_required: number;
  outlets_provided: number;
  /** Number of job_rack_pdus rows (0185) -- not a per-unit outlet count. */
  pdu_count: number;
  btu_typical: number;
  btu_max: number;
  thermal_known_count: number;
  thermal_total_count: number;
  issues: string[];
}

export interface JobRackCheck {
  racks: JobRackCheckResult[];
  warnings: string[];
}

export const jobRackCheck = async (jobId: number, weightLowerThirdPct = 66.7): Promise<JobRackCheck> => {
  const { data, error } = await supabase.rpc("job_rack_check", { p_job_id: jobId, p_weight_lower_third_pct: weightLowerThirdPct });
  if (error) throw error;
  return data as JobRackCheck;
};

export async function fetchJobRacks(
  jobId: number,
): Promise<{ racks: JobRack[]; items: JobRackItem[]; shelves: JobRackShelf[] }> {
  const [{ data: racks, error: rErr }, { data: items, error: iErr }] = await Promise.all([
    supabase.from("job_racks").select("*").eq("job_id", jobId).order("sort_order"),
    supabase.from("job_rack_items").select("*, job_racks!inner(job_id)").eq("job_racks.job_id", jobId).order("sort_order"),
  ]);
  if (rErr) throw rErr;
  if (iErr) throw iErr;

  const rackIds = (racks ?? []).map((r) => r.id);
  // A rack's PDU(s) live in job_pdus now (0540, fetchRackPdus) -- this
  // table (job_rack_pdus) is superseded and permanently empty going
  // forward, so it is no longer fetched here at all.
  const { data: shelves, error: shErr } = rackIds.length
    ? await supabase.from("job_rack_shelves").select("*").in("rack_id", rackIds).order("sort_order")
    : { data: [] as JobRackShelf[], error: null };
  if (shErr) throw shErr;

  return {
    racks: (racks ?? []) as JobRack[],
    items: (items ?? []) as unknown as JobRackItem[],
    shelves: (shelves ?? []) as JobRackShelf[],
  };
}

export async function upsertJobRackShelf(input: {
  shelfId: number | null;
  rackId: number;
  name: string;
  shelfU: number;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_rack_shelf_upsert", {
    p_shelf_id: input.shelfId,
    p_rack_id: input.rackId,
    p_name: input.name,
    p_shelf_u: input.shelfU,
  });
  if (error) throw error;
  return data as number;
}

export async function deleteJobRackShelf(shelfId: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_shelf_delete", { p_shelf_id: shelfId });
  if (error) throw error;
}

/** A Product Library search result, narrowed to what a rack shelf/PDU
 * picker needs to show -- same av_search_profiles RPC the Product Library
 * page itself searches with (0453's frontend follow-up). */
export interface RackAccessoryProductOption {
  id: number;
  model: string | null;
  generic_label: string | null;
  short_description: string | null;
  manufacturer_id: number | null;
}

export async function searchRackAccessoryProducts(query: string): Promise<RackAccessoryProductOption[]> {
  const q = query.trim();
  if (!q) return [];
  const { data, error } = await supabase.rpc("av_search_profiles", { p_query: q, p_status: null });
  if (error) throw error;
  return (data ?? []) as RackAccessoryProductOption[];
}

/** Adds a real Product Library item straight to a live job (0490) --
 * the only way job_equipment_requirements got populated before this was
 * converting quote lines, so a technician on site had nowhere to add a
 * switch (or anything else) once a job existed. Reuses the same real
 * profile-to-row RPC the rack shelf/PDU picker already calls (0453);
 * qty > 1 is split into individual physical units and every resulting
 * unit gets its catalog ports materialised, same as any other multi-unit
 * line. Returns the new job_equipment_requirements id(s). */
export async function addJobEquipmentFromProfile(input: {
  jobId: number;
  jobRoomId: number;
  profileId: number;
  qty: number;
  /** Required to add a kit into a room that already holds parts of that
   * same kit (0522) -- two genuine "+ Add equipment" clicks is exactly
   * how a room ends up with two of an S90. Without this, that second
   * add is refused (error code P0409) rather than silently duplicated. */
  confirmDuplicate?: boolean;
}): Promise<number[]> {
  const { data, error } = await supabase.rpc("job_equipment_add_from_profile", {
    p_job_id: input.jobId,
    p_job_room_id: input.jobRoomId,
    p_profile_id: input.profileId,
    p_qty: input.qty,
    p_confirm_duplicate: input.confirmDuplicate ?? false,
  });
  if (error) throw error;
  return (data ?? []) as number[];
}

/** Removes a piece of equipment from a job. If it's one of a kit's
 * exploded parts (0521 -- an S90 etc, several product categories under
 * one part number), every part of that same kit is removed with it, not
 * left behind as orphans of a kit that's otherwise gone. Returns every
 * job_equipment_requirements id actually removed. */
export async function removeJobEquipment(jobEquipmentId: number): Promise<number[]> {
  const { data, error } = await supabase.rpc("job_equipment_remove", {
    p_job_equipment_id: jobEquipmentId,
  });
  if (error) throw error;
  return (data ?? []) as number[];
}

export interface JobBomMismatch {
  job_room_id: number;
  room_name: string;
  av_product_profile_id: number;
  model: string | null;
  expected_qty: number;
  actual_qty: number;
}

/** Precision check (0522): per room, per component profile, what the WON
 * quote's kit lines say should be here vs. what's actually in
 * job_equipment_requirements right now. Empty = reconciles -- a
 * mismatch means either a kit is missing parts, or more of it exists
 * than was ever quoted (e.g. a duplicate "+ Add equipment"). */
export async function fetchJobBomReconciliation(jobId: number): Promise<JobBomMismatch[]> {
  const { data, error } = await supabase.rpc("job_bom_reconciliation", { p_job_id: jobId });
  if (error) throw error;
  return (data ?? []) as JobBomMismatch[];
}

/** Links a shelf to a real Product Library product -- creates (or points
 * at) a real job_equipment_requirements row so it shows up in Kit
 * Balance/the Equipment tab (0453), which neither reads job_rack_shelves
 * directly. A null profile id clears the link; the requirement row, if
 * any, is left alone rather than deleted. */
export async function setJobRackShelfProduct(shelfId: number, avProductProfileId: number | null): Promise<number | null> {
  const { data, error } = await supabase.rpc("job_rack_shelf_set_product", {
    p_shelf_id: shelfId,
    p_av_product_profile_id: avProductProfileId,
  });
  if (error) throw error;
  return (data as number | null) ?? null;
}

/** One item's mount -- native rackmount, on a named shelf, rear-mounted,
 * zero-U, loose, or other -- without touching its sort_order or any
 * other item in the rack. setJobRackItems (bulk reorder) never carried
 * this. */
export async function setJobRackItemMount(input: {
  jobEquipmentId: number;
  mountMethod: JobRackItemMountMethod;
  shelfId: number | null;
  dedicatedShelfU: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc("job_rack_item_set_mount", {
    p_job_equipment_id: input.jobEquipmentId,
    p_mount_method: input.mountMethod,
    p_shelf_id: input.shelfId,
    p_dedicated_shelf_u: input.dedicatedShelfU,
  });
  if (error) throw error;
}

/** Sets (or corrects) rack_unit_size on the Product Library profile
 * behind one job item -- "ask and remember" from the rack builder
 * itself, rather than a trip to the Product Library. Overwrites a
 * height already on file too (0447) -- the rack builder is where a
 * wrong one gets noticed. */
export async function setJobRackItemRackU(jobEquipmentId: number, rackUnitSize: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_item_set_rack_u", {
    p_job_equipment_id: jobEquipmentId,
    p_rack_unit_size: rackUnitSize,
  });
  if (error) throw error;
}

/** Pins an item to an explicit U offset from the top of the rack (or
 * null to unpin it back into the packed flow) -- how a real gap gets
 * left in the elevation instead of everything always packing from the
 * top (0449). */
export async function setJobRackItemStartU(jobEquipmentId: number, startU: number | null): Promise<void> {
  const { error } = await supabase.rpc("job_rack_item_set_start_u", {
    p_job_equipment_id: jobEquipmentId,
    p_start_u: startU,
  });
  if (error) throw error;
}

/** Pins a shelf to an explicit U offset from the top of the rack (or
 * null to unpin it back into the packed flow) -- mirrors
 * setJobRackItemStartU (0449), so a shelf can leave a real gap too,
 * not just an item (0450). */
export async function setJobRackShelfStartU(shelfId: number, startU: number | null): Promise<void> {
  const { error } = await supabase.rpc("job_rack_shelf_set_start_u", {
    p_shelf_id: shelfId,
    p_start_u: startU,
  });
  if (error) throw error;
}

/** The Product Library's power_method enum (av_product_profiles_power_
 * method_check), for the "power supply unknown -- set it" picker
 * (0454). Kept here as the one place both the picker's options and
 * anything else reading power_method can share, rather than each
 * screen inventing its own label text for the same closed set. */
export const POWER_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: "direct_230v", label: "Direct 230V" },
  { value: "external_psu", label: "External PSU" },
  { value: "poe", label: "PoE" },
  { value: "poe_plus", label: "PoE+" },
  { value: "poe_pp", label: "PoE++" },
  { value: "usb_powered", label: "USB-powered" },
  { value: "dc_from_device", label: "DC from another device" },
  { value: "passive", label: "Passive (no power)" },
  { value: "hardwired", label: "Hardwired" },
  { value: "battery", label: "Battery" },
  { value: "custom", label: "Custom / other" },
];

/** Fills (or corrects) power_method on the Product Library profile
 * behind one job item -- "ask and remember" from wherever someone's
 * already looking at the box, mirroring setJobRackItemRackU (0447). */
export async function setJobEquipmentPowerMethod(jobEquipmentId: number, powerMethod: string): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_set_power_method", {
    p_job_equipment_id: jobEquipmentId,
    p_power_method: powerMethod,
  });
  if (error) throw error;
}

export async function upsertJobRack(input: {
  rackId: number | null;
  jobId: number;
  roomLabel: string | null;
  jobRoomId: number | null;
  label: string;
  rackType: JobRackType;
  totalU: number | null;
  hasFanTray: boolean;
  hasActiveVentilation: boolean;
  roomAirConditioned: JobRackAirConditioned;
  environmentNotes: string | null;
  clientRackKnownOccupiedU: number | null;
  clientRackKnownAvailableU: number | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_rack_upsert", {
    p_rack_id: input.rackId,
    p_job_id: input.jobId,
    p_room_label: input.roomLabel,
    p_label: input.label,
    p_rack_type: input.rackType,
    p_total_u: input.totalU,
    p_has_fan_tray: input.hasFanTray,
    p_has_active_ventilation: input.hasActiveVentilation,
    p_room_air_conditioned: input.roomAirConditioned,
    p_pdu_count: null,
    p_pdu_outlets_per_unit: null,
    p_environment_notes: input.environmentNotes,
    p_job_room_id: input.jobRoomId,
    p_client_rack_known_occupied_u: input.clientRackKnownOccupiedU,
    p_client_rack_known_available_u: input.clientRackKnownAvailableU,
  });
  if (error) throw error;
  return data as number;
}

/**
 * Where a rack is: one physical room, or one equipment store.
 *
 * A direct write rather than an RPC because since 0232 this IS the fact
 * -- job_room_id and room_label are mirrors a trigger derives from it,
 * and job_rack_upsert's p_job_room_id writes a value that same trigger
 * immediately overwrites. Every rack the Rack tab made belonged to
 * nowhere for that reason.
 *
 * app.rack_validate still has the last word: a credenza only in a room
 * that declared one, no credenza or table in the store, five racks to a
 * room. rackPlacement.ts says the same things first so somebody reads an
 * answer rather than an error.
 */
export async function setRackPlace(
  rackId: number,
  place: {
    spaceId: number | null;
    storeId: number | null;
    installationType: string | null;
  },
): Promise<void> {
  const { error } = await supabase
    .from("job_racks")
    .update({
      space_id: place.spaceId,
      equipment_store_id: place.storeId,
      installation_type: place.installationType,
    })
    .eq("id", rackId);
  if (error) throw error;
}

export async function deleteJobRack(rackId: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_delete", { p_rack_id: rackId });
  if (error) throw error;
}

export async function setJobRackItems(rackId: number, items: { job_equipment_id: number; sort_order: number; notes: string | null }[]): Promise<void> {
  const { error } = await supabase.rpc("job_rack_set_items", { p_rack_id: rackId, p_items: items });
  if (error) throw error;
}

export type JobRackElevationRow = { kind: "item"; job_equipment_id: number } | { kind: "shelf"; shelf_id: number };

/** Reorders the elevation's own draggable sequence -- rackmounted items
 * AND shelves-as-units, interleaved -- top to bottom. UPDATE-only (0448):
 * unlike setJobRackItems, a row missing from the list is left exactly
 * where it was, so this can't silently drop a shelved/off-U item the
 * way a plain full-replace reorder once did. */
export async function setJobRackElevation(rackId: number, rows: JobRackElevationRow[]): Promise<void> {
  const { error } = await supabase.rpc("job_rack_set_elevation", { p_rack_id: rackId, p_rows: rows });
  if (error) throw error;
}

/** Atomic move -- covers both a single drag-to-another-rack action and
 * batch reassignment ("select all equipment from Meeting Room 01 -> assign
 * to Rack 01"). Appends to the end of the destination rack; use
 * setJobRackItems for a precise position within one rack. */
export async function assignJobRackItems(jobEquipmentIds: number[], toRackId: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_assign_items", { p_job_equipment_ids: jobEquipmentIds, p_to_rack_id: toRackId });
  if (error) throw error;
}

/** Proposes an initial stacking order for a rack's current items (0141):
 * client-accessible kit to the top, an amplifier second-from-bottom,
 * PDUs at the very bottom, everything else by weight in between
 * (0451). Only ever touches sort_order -- a pinned or shelved item is
 * unaffected. */
export async function jobRackAutoLayout(rackId: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_auto_layout", { p_rack_id: rackId });
  if (error) throw error;
}

/** Recomputes sort_order for every item in the rack from Product Library
 * metadata (client-accessible toward the top, PDUs toward the bottom,
 * everything else lightest-to-heaviest) -- a starting point, not a lock;
 * a person can still reorder afterwards. */
export async function autoLayoutJobRack(rackId: number): Promise<void> {
  const { error } = await supabase.rpc("job_rack_auto_layout", { p_rack_id: rackId });
  if (error) throw error;
}

/** Stamps the current planned order as the as-physically-installed
 * position (0153's actual_sort_order) -- a one-off "confirm on site"
 * action, not something reorder/auto-layout touch. Plain table writes:
 * job_rack_items already grants can_manage_projects/can_manage_wip write
 * access directly, no RPC needed for a single-column stamp. */
export async function confirmJobRackAsInstalled(items: { id: number; sort_order: number }[]): Promise<void> {
  const results = await Promise.all(
    items.map((i) => supabase.from("job_rack_items").update({ actual_sort_order: i.sort_order }).eq("id", i.id)),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

/* ---------------------------------------------------------- system schematic */

/** The structured connectivity view (0142) -- a per-room, per-device list
 * of ports and what each connects to, chosen over a visual drag-and-drop
 * schematic canvas for this pass. Network uplinks carried forward from the
 * quote stage are seeded automatically (jobSchematicGenerate); everything
 * else (video/audio routing) has no equivalent prior data to infer from,
 * so it's added by hand rather than guessed at. */
export interface JobSchematicConnection {
  connection_id: number;
  to_equipment_id: number | null;
  to_equipment_description: string | null;
  to_port_name: string | null;
  to_external_label: string | null;
  connection_type: string;
  is_auto_generated: boolean;
  notes: string | null;
}

export interface JobSchematicPort {
  port_name: string;
  direction: "input" | "output" | "bidirectional";
  connector_label: string | null;
  signal_types: string[];
  network_protocols: string[];
  is_poe: boolean;
  poe_role: "pd" | "pse" | null;
  port_quantity: number;
  connections: JobSchematicConnection[];
}

export interface JobSchematicEquipment {
  equipment_id: number;
  description: string;
  model_no: string | null;
  manufacturer_name: string | null;
  av_product_profile_id: number | null;
  ports: JobSchematicPort[];
}

export interface JobSchematicRoom {
  room_name: string;
  equipment: JobSchematicEquipment[];
}

export interface JobSchematic {
  rooms: JobSchematicRoom[];
}

export async function fetchJobSchematic(jobId: number): Promise<JobSchematic> {
  const { data, error } = await supabase.rpc("job_schematic_data", { p_job_id: jobId });
  if (error) throw error;
  return data as JobSchematic;
}

export async function generateJobSchematic(jobId: number): Promise<{ connections_created: number }> {
  const { data, error } = await supabase.rpc("job_schematic_generate", { p_job_id: jobId });
  if (error) throw error;
  return data as { connections_created: number };
}

export async function setJobSchematicConnection(input: {
  jobId: number;
  fromEquipmentId: number;
  fromPortName: string;
  toEquipmentId: number | null;
  toPortName: string | null;
  toExternalLabel: string | null;
  connectionType: string;
  notes: string | null;
  signalLabel?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_schematic_set_connection", {
    p_job_id: input.jobId,
    p_from_equipment_id: input.fromEquipmentId,
    p_from_port_name: input.fromPortName,
    p_to_equipment_id: input.toEquipmentId,
    p_to_port_name: input.toPortName,
    p_to_external_label: input.toExternalLabel,
    p_connection_type: input.connectionType,
    p_notes: input.notes,
    p_signal_label: input.signalLabel ?? null,
  });
  if (error) throw error;
  return data as number;
}

export async function deleteJobSchematicConnection(connectionId: number): Promise<void> {
  const { error } = await supabase.rpc("job_schematic_delete_connection", { p_connection_id: connectionId });
  if (error) throw error;
}

/* --------------------------------------------------- room schematic (0155) */

/** Connection state -- see job_equipment_connections.state (0155).
 * confirmed: a human signed off. auto_proposed: the topology engine
 * generated this and it hasn't been reviewed. needs_input: a likely
 * relationship was identified but no endpoint could be safely resolved
 * (surfaced as text, never drawn as an edge to nowhere). manual: an
 * engineer created this by hand. */
// Aliased rather than redeclared: connectionState.ts owns the vocabulary
// and the predicates that go with it, and two lists of the same eight
// strings is how one of them ends up short. Imported as well as
// re-exported, because a bare `export type ... from` does not put the
// name in this module's own scope and the types below use it.
import type { ConnectionState } from "./connectionState";
export type { ConnectionState as SchematicConnectionState } from "./connectionState";
type SchematicConnectionState = ConnectionState;

/** Physical placement on a room's schematic (0183) -- null renders as
 * "Needs location", never inferred beyond the cautious preferred_zones
 * suggestion offered in the device drawer. */
export type SchematicZone =
  | "wall" | "ceiling" | "table" | "floor" | "credenza" | "lectern"
  | "rack" | "comms_cab" | "outdoor" | "presentation_area" | "audience_area";

export type SchematicConnectionDirection = "source_to_dest" | "bidirectional";
/** Where a connection's confidence score came from (0183) -- viewpoint_rule
 * is the only source in use today; the rest are reserved for later phases
 * (historic-precedent matching, AI-assisted inference). */
export type SchematicConfidenceSource =
  | "manufacturer" | "viewpoint_rule" | "approved_historic" | "similar_historic" | "ai_inference" | "manual";

/** Per-PHYSICAL-port state on one job equipment instance (0187).
 *
 * Distinct from `JobSchematicPort`, which is the older product-catalogue
 * shape: this is the instance the schematic actually connects to, one row
 * per real port, so a switch's eight ports are eight of these rather than
 * one entry with port_quantity 8.
 *
 * `status` is derived server-side and is the value to display: a port with
 * any connection reads "connected" whatever `intent` says, so the two can
 * never contradict each other. `intent` alone is what a human decided
 * about a port with no cable -- and "unknown" is deliberately NOT the same
 * as "not connected". */
export type SchematicPortStatus = "connected" | "nc" | "reserved" | "unknown";
export type SchematicPortIntent = "unknown" | "nc" | "reserved";
export type SchematicPortSignalType =
  | "video" | "audio" | "network" | "usb" | "control" | "speaker" | "power" | "multi_signal" | "other";

export interface SchematicPortLink {
  connection_id: number;
  state: SchematicConnectionState;
  /** Which end of the stored connection this port sits on. One connection
   * row, read from both sides -- never two mirrored rows. */
  side: "in" | "out";
  connection_type: string;
  signal_label: string | null;
  other_description: string | null;
  other_port_name: string | null;
}

export interface RoomSchematicPort {
  port_id: number;
  port_name: string;
  port_group: string | null;
  direction: "input" | "output" | "bidirectional";
  signal_type: SchematicPortSignalType;
  connector_key: string | null;
  is_poe: boolean;
  poe_role: "pd" | "pse" | null;
  schematic_relevant: boolean;
  intent: SchematicPortIntent;
  status: SchematicPortStatus;
  links: SchematicPortLink[];
}

/** Room-level port counts. Returned as components rather than a single
 * percentage so the "how much is a proposed connection worth" policy lives
 * in one place on the client instead of baked into SQL. */
export interface RoomPortSummary {
  relevant: number;
  connected_confirmed: number;
  connected_proposed: number;
  nc: number;
  reserved: number;
  unknown: number;
  devices_needing_port_data: number;
}

export interface RoomSchematicConnection {
  connection_id: number;
  from_equipment_id: number;
  from_port_name: string;
  from_port_id: number | null;
  to_equipment_id: number | null;
  to_equipment_description: string | null;
  to_port_name: string | null;
  to_port_id: number | null;
  to_external_label: string | null;
  connection_type: string;
  signal_label: string | null;
  state: SchematicConnectionState;
  is_auto_generated: boolean;
  topology_rule_id: number | null;
  notes: string | null;
  direction: SchematicConnectionDirection;
  confidence: number | null;
  confidence_source: SchematicConfidenceSource | null;
  rule_confidence: TopologyRuleConfidence | null;
  rule_notes: string | null;
  rule_provenance: string | null;
}

export interface RoomSchematicEquipment {
  equipment_id: number;
  description: string;
  model_no: string | null;
  manufacturer_name: string | null;
  manufacturer_id: number | null;
  av_product_profile_id: number | null;
  profile_model: string | null;
  product_family: string | null;
  category: string | null;
  equipment_role: EquipmentRole | null;
  secondary_roles: EquipmentRole[];
  network_role: string | null;
  default_network_requirement: string | null;
  attributes: Record<string, unknown>;
  image_url: string | null;
  schematic_x: number | null;
  schematic_y: number | null;
  schematic_zone: SchematicZone | null;
  preferred_zones: SchematicZone[];
  ports: RoomSchematicPort[];
  /** True when this device has no materialised ports at all -- the
   * product profile carries no port data. Surfaced as "port data
   * required" rather than papered over with invented ports. */
  port_data_required: boolean;
  /** Physically lives in a DIFFERENT room from the one being viewed --
   * a shared DSP/switch/control processor in a comms-room rack serving
   * this room (0190). It appears on this room's schematic because it is
   * part of this room's signal path, but it is one equipment row shared
   * across rooms, never a copy. */
  is_remote: boolean;
  physical_room_id: number | null;
  physical_room_name: string | null;
  /** How many rooms this device serves in total (1 = just its own). */
  serves_room_count: number;
}

/** Set (or clear, passing null) a device's physical placement on its
 * room's schematic. */
export async function setSchematicZone(equipmentId: number, zone: SchematicZone | null): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_set_schematic_zone", { p_equipment_id: equipmentId, p_zone: zone });
  if (error) throw error;
}

export interface RoomSchematicData {
  room: { id: number; job_id: number; name: string } | null;
  equipment: RoomSchematicEquipment[];
  connections: RoomSchematicConnection[];
  port_summary: RoomPortSummary;
}

export async function fetchJobRoomSchematic(jobRoomId: number): Promise<RoomSchematicData> {
  const { data, error } = await supabase.rpc("job_room_schematic_data", { p_job_room_id: jobRoomId });
  if (error) throw error;
  return data as RoomSchematicData;
}

/** Materialise per-physical-port rows for every device in a room from the
 * product catalogue. Idempotent and additive -- re-running never deletes a
 * port or the engineering state on it, so it is safe to call on open. */
export async function materializeRoomPorts(jobRoomId: number): Promise<{ ports_created: number; devices_needing_port_data: number }> {
  const { data, error } = await supabase.rpc("job_room_materialize_ports", { p_job_room_id: jobRoomId });
  if (error) throw error;
  return data as { ports_created: number; devices_needing_port_data: number };
}

/** Link (or unlink) a device to a room it SERVES but doesn't sit in --
 * the shared central rack case. Creates no second equipment row: the same
 * device appears on both rooms' schematics, with one commissioning state,
 * one rack slot and one entry in the BOM. */
export async function setEquipmentRoomLink(equipmentId: number, jobRoomId: number, linked: boolean): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_set_room_link", {
    p_equipment_id: equipmentId,
    p_job_room_id: jobRoomId,
    p_linked: linked,
  });
  if (error) throw error;
}

/** Record what an engineer decided about a port that carries no cable.
 * Never inferred: a port stays "unknown" until somebody says otherwise. */
export async function setPortIntent(portId: number, intent: SchematicPortIntent): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_port_set_intent", { p_port_id: portId, p_intent: intent });
  if (error) throw error;
}

/** "I've reviewed this device -- everything still unresolved genuinely
 * isn't used." Marks every unknown, unconnected, schematic-relevant port
 * on one device as N/C, which is otherwise a tap per port. */
export async function markRemainingPortsNc(equipmentId: number): Promise<{ updated: number }> {
  const { data, error } = await supabase.rpc("job_equipment_ports_mark_rest_nc", { p_equipment_id: equipmentId });
  if (error) throw error;
  return data as { updated: number };
}

/** Connect one physical port to another (or to an external endpoint such
 * as "Client LAN"). Writes ONE connection row -- both ports read their
 * state from it, so disconnecting frees both ends and there is no second
 * mirrored record to drift. */
export async function connectPorts(input: {
  fromPortId: number;
  toPortId: number | null;
  toExternalLabel?: string | null;
  connectionType?: string | null;
  signalLabel?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_schematic_connect_ports", {
    p_from_port_id: input.fromPortId,
    p_to_port_id: input.toPortId,
    p_to_external_label: input.toExternalLabel ?? null,
    p_connection_type: input.connectionType ?? null,
    p_signal_label: input.signalLabel ?? null,
  });
  if (error) throw error;
  return data as number;
}

export type TopologyRuleScope = "exact_model" | "product_family" | "role";
export type TopologyRuleConfidence = "exact" | "high" | "medium" | "low";

export interface TopologyRule {
  id: number;
  scope: TopologyRuleScope;
  from_manufacturer_id: number | null;
  from_model: string | null;
  from_product_family: string | null;
  from_role: EquipmentRole | null;
  from_port_label: string | null;
  to_manufacturer_id: number | null;
  to_model: string | null;
  to_product_family: string | null;
  to_role: EquipmentRole | null;
  to_port_label: string | null;
  signal_label: string;
  connection_category: string;
  direction: "source_to_dest" | "bidirectional";
  required: boolean;
  same_room_required: boolean;
  multiplicity: "one_to_one" | "one_to_many" | "many_to_one";
  confidence: TopologyRuleConfidence;
  manufacturer_family: string | null;
  notes: string | null;
}

/** Small, cacheable reference table -- fetched once and reused across every
 * room's generation pass, not re-queried per room. */
export async function fetchTopologyRules(): Promise<TopologyRule[]> {
  const { data, error } = await supabase
    .from("av_topology_rules")
    .select(
      "id, scope, from_manufacturer_id, from_model, from_product_family, from_role, from_port_label, to_manufacturer_id, to_model, to_product_family, to_role, to_port_label, signal_label, connection_category, direction, required, same_room_required, multiplicity, confidence, manufacturer_family, notes",
    )
    .eq("is_active", true)
    .eq("status", "approved");
  if (error) throw error;
  return (data ?? []) as TopologyRule[];
}

export interface GeneratedConnectionInput {
  from_equipment_id: number;
  from_port_name?: string | null;
  /** Since 0187 the generator resolves a real physical port at each end
   * and never emits a connection it could not resolve, so these carry the
   * actual port rows rather than a synthesised "HDMI out" label. */
  from_port_id?: number | null;
  to_equipment_id?: number | null;
  to_port_name?: string | null;
  to_port_id?: number | null;
  to_external_label?: string | null;
  connection_type: string;
  signal_label?: string | null;
  state: "auto_proposed" | "needs_input";
  topology_rule_id?: number | null;
  notes?: string | null;
  direction?: SchematicConnectionDirection;
  confidence?: number | null;
  confidence_source?: SchematicConfidenceSource | null;
}

/** Idempotent: replaces only this room's own auto_proposed/needs_input rows
 * -- a confirmed or manually-created connection is never touched. */
export async function saveGeneratedSchematic(jobRoomId: number, connections: GeneratedConnectionInput[]): Promise<{ connections_created: number }> {
  const { data, error } = await supabase.rpc("job_schematic_save_generated", {
    p_job_room_id: jobRoomId,
    p_connections: connections,
  });
  if (error) throw error;
  return data as { connections_created: number };
}

export async function confirmSchematicConnection(connectionId: number): Promise<void> {
  const { error } = await supabase.rpc("job_schematic_confirm_connection", { p_connection_id: connectionId });
  if (error) throw error;
}

export async function updateSchematicPosition(equipmentId: number, x: number, y: number): Promise<void> {
  const { error } = await supabase.from("job_equipment_requirements").update({ schematic_x: x, schematic_y: y }).eq("id", equipmentId);
  if (error) throw error;
}

/* ---------------------------------------------- schematic review agent (0184) */
// Phase A of the "agentic schematic" pack: an LLM-backed reviewer that
// flags engineering ambiguities/documentation gaps without ever silently
// changing confirmed data (see supabase/functions/schematic-review). This
// is a review LAYER on top of the deterministic generator above -- it
// never replaces generateRoomConnections, and accepting a proposed change
// always goes through the existing connection RPCs (confirmSchematicConnection/
// deleteJobSchematicConnection), never a bespoke "apply AI change" path.

export type SchematicReviewReadiness = "ready" | "ready_with_warnings" | "needs_input" | "blocked";
export type SchematicFindingSeverity = "info" | "warning" | "error";
export type SchematicFindingCategory =
  | "topology" | "network" | "port" | "location" | "shared_resource" | "documentation" | "layout" | "historic_precedent" | "bom_consistency";
export type SchematicEvidenceType = "manufacturer" | "viewpoint_rule" | "approved_historic" | "project_data" | "ai_inference";

export interface SchematicFindingEvidence {
  type: SchematicEvidenceType;
  reference?: string | null;
}

export interface SchematicReviewFinding {
  id: number;
  review_id: number;
  severity: SchematicFindingSeverity;
  category: SchematicFindingCategory;
  title: string;
  description: string;
  affected_device_ids: number[];
  affected_connection_ids: number[];
  evidence: SchematicFindingEvidence[];
  requires_human_decision: boolean;
  dismissed_at: string | null;
}

export interface ProposedEngineeringChange {
  id: number;
  review_id: number;
  change_type: string;
  reason: string;
  affected_connection_id: number | null;
  payload: Record<string, unknown>;
  confidence: number;
  status: "pending" | "accepted" | "rejected";
}

export interface SchematicReview {
  id: number;
  job_room_id: number;
  readiness: SchematicReviewReadiness;
  summary: string;
  model: string;
  equipment_count: number;
  connection_count: number;
  created_at: string;
  findings: SchematicReviewFinding[];
  proposedChanges: ProposedEngineeringChange[];
}

export interface RunSchematicReviewResult {
  ok: boolean;
  review_id?: number;
  error?: string;
}

const SCHEMATIC_REVIEW_POLL_MS = 1500;
const SCHEMATIC_REVIEW_POLL_LIMIT = 40; // ~60s -- a multi-category structured review runs longer than av-product-research's single-fact lookup.

/** Same relay transport as requestAiResearch (products/api.ts) -- the
 * browser never talks to the edge function directly, only via
 * app.request_manual_sync's pg_net relay + a manual_sync_result poll. */
export async function runSchematicReview(jobRoomId: number): Promise<RunSchematicReviewResult> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "schematic-review",
    p_body: { job_room_id: jobRoomId },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: RunSchematicReviewResult } = { done: false };
  for (let i = 0; i < SCHEMATIC_REVIEW_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, SCHEMATIC_REVIEW_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("Schematic review is taking longer than expected — check back shortly.");
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(outcome.body?.error ?? `Schematic review failed (${outcome.status}).`);
  }
  return outcome.body ?? { ok: false, error: "No response from schematic review" };
}

export interface SchematicPdfResult {
  ok: boolean;
  error?: string;
  signed_url?: string | null;
  file_id?: number;
  devices?: number;
  connections?: number;
}

const SCHEMATIC_PDF_POLL_MS = 1500;
const SCHEMATIC_PDF_POLL_LIMIT = 40; // ~60s -- pdf-lib render + storage upload.

/** Renders the SAVED topology to PDF. Same relay transport as
 * runSchematicReview: managed devices in this fleet block browser POSTs
 * straight to /functions/v1, so it goes via request_manual_sync's pg_net
 * relay and a manual_sync_result poll.
 *
 * The signed URL it returns is short-lived by design -- it is a link to
 * hand to the browser now, not something to store. */
export async function generateSchematicPdf(jobRoomId: number): Promise<SchematicPdfResult> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "generate-schematic-pdf",
    p_body: { job_room_id: jobRoomId },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: SchematicPdfResult } = { done: false };
  for (let i = 0; i < SCHEMATIC_PDF_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, SCHEMATIC_PDF_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("The PDF is taking longer than expected \u2014 check back shortly.");
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(outcome.body?.error ?? `Generating the PDF failed (${outcome.status}).`);
  }
  return outcome.body ?? { ok: false, error: "No response from the PDF generator" };
}

/* ------------------------------------------- Output tab (0403/0404) */

/**
 * One line per thing the Output tab checks -- Equipment, Connections,
 * Cable lengths, Power, Rack, and LED only when the room has LED gear.
 * `done`/`total` are already the RIGHT denominator (required ports, not
 * every port; rack-located devices, not every device) -- this is a
 * display of app.job_room_output_checklist, not a second opinion on it.
 */
/** Where a tapped outstanding item jumps to and what it highlights once
 * there (0465) -- 'equipment' covers both the Equipment and Power tabs
 * (both key their device cards by the same job_equipment_requirements
 * id), 'cable' is a job_cables id for Cabling's route cards. Connections/
 * Rack/LED items carry no target yet -- that pass is scoped to the three
 * checks above, deliberately, not a gap nobody noticed. */
export interface OutstandingTarget {
  type: "equipment" | "cable";
  id: number;
}

export interface OutstandingItem {
  label: string;
  target?: OutstandingTarget;
}

export interface RoomOutputCheck {
  key: string;
  label: string;
  done: number;
  total: number;
  ok: boolean;
  /** What's actually still outstanding, named -- a device reference, a
   * "device - port" pair, a cable ID (0458), now with a target to jump
   * straight to and highlight where the checklist has one (0465). */
  outstanding: OutstandingItem[];
}

export interface RoomOutputStatus {
  job_room_id: number;
  room_name: string;
  checks: RoomOutputCheck[];
  overall_pct: number;
  all_ok: boolean;
  /** draft | complete | changed -- what the room's documents would be
   *  watermarked with if generated right now. */
  status: "draft" | "complete" | "changed";
  fingerprint: string;
}

/** app.job_room_output_checklist's `outstanding` entries are a plain
 * string for a check 0465 didn't touch (Connections/Rack/LED) and a
 * {label, target} object for one it did (Equipment/Cabling/Power) --
 * normalized here once, at the fetch boundary, so nothing downstream has
 * to know the RPC returns a mixed shape. */
function normalizeOutstanding(raw: unknown): OutstandingItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => (typeof item === "string" ? { label: item } : (item as OutstandingItem)));
}

export async function fetchRoomOutputStatus(jobRoomId: number): Promise<RoomOutputStatus> {
  const { data, error } = await supabase.rpc("job_room_output_checklist", { p_job_room_id: jobRoomId });
  if (error) throw new Error(error.message);
  const raw = data as Omit<RoomOutputStatus, "checks"> & { checks: (Omit<RoomOutputCheck, "outstanding"> & { outstanding: unknown })[] };
  return {
    ...raw,
    checks: raw.checks.map((c) => ({ ...c, outstanding: normalizeOutstanding(c.outstanding) })),
  };
}

export type RoomDocumentKind = "full_spec" | "cable_schedule" | "power_schedule";

export interface RoomDocumentResult {
  ok: boolean;
  error?: string;
  signed_url?: string | null;
  file_id?: number;
  status?: RoomOutputStatus["status"];
  overall_pct?: number;
}

const ROOM_DOCUMENT_POLL_MS = 1500;
const ROOM_DOCUMENT_POLL_LIMIT = 40; // ~60s -- same budget as the schematic PDF.

/** Full AV Room Specification / Cable Schedule / Power Schedule -- all
 * three are this same edge function reading app.job_room_output_record,
 * relayed the same way generateSchematicPdf is (managed devices in this
 * fleet cannot POST to /functions/v1 directly). Generating any one of
 * them also records a design-revision checkpoint (0403), which is why
 * the result carries the status/overall_pct the document was watermarked
 * with. */
export async function generateRoomDocument(
  jobRoomId: number,
  documentKind: RoomDocumentKind,
): Promise<RoomDocumentResult> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "generate-room-document",
    p_body: { job_room_id: jobRoomId, document_kind: documentKind },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: RoomDocumentResult } = { done: false };
  for (let i = 0; i < ROOM_DOCUMENT_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, ROOM_DOCUMENT_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("The document is taking longer than expected \u2014 check back shortly.");
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(outcome.body?.error ?? `Generating the document failed (${outcome.status}).`);
  }
  return outcome.body ?? { ok: false, error: "No response from the document generator" };
}

export interface JobFullScheduleResult {
  ok: boolean;
  error?: string;
  /** Present on a reconciliation failure (0444) -- what didn't add up,
   *  not just that something didn't. */
  details?: string[];
  signed_url?: string | null;
  file_id?: number;
  room_count?: number;
  total_equipment_qty?: number;
  overall_pct?: number;
}

/** The Full Schedule -- every room on the job, not just whichever one is
 * currently open (0444). Same edge function and relay as
 * generateRoomDocument, a different document_kind and job_id in place of
 * job_room_id; the single-room documents are unaffected by this and stay
 * available as "Current Room Specification". */
export async function generateJobFullSchedule(jobId: number): Promise<JobFullScheduleResult> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "generate-room-document",
    p_body: { job_id: jobId, document_kind: "full_schedule" },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: JobFullScheduleResult } = { done: false };
  for (let i = 0; i < ROOM_DOCUMENT_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, ROOM_DOCUMENT_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("The document is taking longer than expected \u2014 check back shortly.");
  if (outcome.status !== undefined && outcome.status >= 400) {
    const details = outcome.body?.details;
    const message = outcome.body?.error ?? `Generating the Full Schedule failed (${outcome.status}).`;
    throw new Error(details && details.length > 0 ? `${message} ${details.join(" ")}` : message);
  }
  return outcome.body ?? { ok: false, error: "No response from the document generator" };
}

export async function fetchLatestSchematicReview(jobRoomId: number): Promise<SchematicReview | null> {
  const { data: review, error } = await supabase
    .from("schematic_reviews")
    .select("*")
    .eq("job_room_id", jobRoomId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!review) return null;

  const [findingsRes, changesRes] = await Promise.all([
    supabase.from("schematic_review_findings").select("*").eq("review_id", review.id).order("sort_order"),
    supabase.from("schematic_review_proposed_changes").select("*").eq("review_id", review.id),
  ]);
  if (findingsRes.error) throw findingsRes.error;
  if (changesRes.error) throw changesRes.error;

  return {
    ...review,
    findings: (findingsRes.data ?? []) as SchematicReviewFinding[],
    proposedChanges: (changesRes.data ?? []) as ProposedEngineeringChange[],
  } as SchematicReview;
}

export async function dismissReviewFinding(findingId: number): Promise<void> {
  const { error } = await supabase.rpc("schematic_review_dismiss_finding", { p_finding_id: findingId });
  if (error) throw error;
}

export async function decideProposedChange(changeId: number, decision: "accepted" | "rejected"): Promise<void> {
  const { error } = await supabase.rpc("schematic_review_decide_proposed_change", { p_change_id: changeId, p_decision: decision });
  if (error) throw error;
}

/* ------------------------------------------- connection schedule (0217) */

/**
 * One flat row per cable — the shape an installer reads and a phone can
 * show, as opposed to the device-centric payload the drawing needs.
 * `is_resolved` and friends are computed in SQL alongside the sort order,
 * so the browser never re-derives a classification the database already
 * made.
 */
export interface ScheduleRow {
  id: number;
  state: SchematicConnectionState;
  connection_type: string | null;
  signal_label: string | null;
  notes: string | null;
  cable_reference: string | null;
  cable_length_m: number | null;
  cable_route: string | null;
  /** What the two devices' locations say the run is (0229). Null while
   * either end is unplaced. The stored cable_route can differ -- once
   * somebody types over a route it is protected forever (0225) -- and the
   * editor offers this back as the way to undo that. */
  derived_route: string | null;
  external_destination_type: string | null;
  confirmed_at: string | null;
  from_equipment_id: number | null;
  from_reference: string | null;
  from_description: string | null;
  from_port_id: number | null;
  from_port: string | null;
  from_direction: string | null;
  from_signal_type: string | null;
  to_equipment_id: number | null;
  to_reference: string | null;
  to_description: string | null;
  to_port_id: number | null;
  to_port: string | null;
  to_direction: string | null;
  to_signal_type: string | null;
  to_external_label: string | null;
  is_resolved: boolean;
  is_reserved: boolean;
  is_proposal: boolean;
  needs_input: boolean;
}

export interface DesignFinding {
  severity: "blocking" | "needs_confirmation" | "advisory";
  code: string;
  subject: string;
  detail: string;
}

export interface RoomDesignStatus {
  job_room_id: number;
  devices: number;
  completeness: {
    required_ports: number;
    required_ports_linked: number;
    required_ports_confirmed: number;
    connections_total: number;
    connections_confirmed: number;
    connections_proposed: number;
    connections_needing_input: number;
    /** Null when nothing is required yet — 0% would claim outstanding work. */
    percent: number | null;
    nothing_required_yet: boolean;
  };
  findings: DesignFinding[];
  counts: { blocking: number; needs_confirmation: number; advisory: number };
}

export interface CompatiblePort {
  port_id: number;
  port_name: string;
  direction: string;
  signal_type: string;
  is_poe: boolean;
  poe_role: string | null;
  equipment_id: number;
  device_reference: string | null;
  device_description: string;
  project_relevance: string;
}

export async function fetchConnectionSchedule(jobRoomId: number): Promise<ScheduleRow[]> {
  const { data, error } = await supabase.rpc("job_room_connection_schedule", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as ScheduleRow[];
}

export async function fetchRoomDesignStatus(jobRoomId: number): Promise<RoomDesignStatus> {
  const { data, error } = await supabase.rpc("job_room_design_status", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw new Error(error.message);
  return data as RoomDesignStatus;
}

export async function fetchCompatiblePorts(
  jobRoomId: number,
  fromPortId: number,
): Promise<CompatiblePort[]> {
  const { data, error } = await supabase.rpc("job_room_compatible_ports", {
    p_job_room_id: jobRoomId,
    p_from_port_id: fromPortId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as CompatiblePort[];
}

export interface ConnectionDetailsPatch {
  state?: SchematicConnectionState;
  signal_label?: string | null;
  cable_reference?: string | null;
  cable_length_m?: number | null;
  cable_route?: string | null;
  external_destination_type?: string | null;
  notes?: string | null;
}

/** Undefined means leave alone — the RPC coalesces, so omitting a field
 * cannot blank it. */
export async function setConnectionDetails(
  connectionId: number,
  patch: ConnectionDetailsPatch,
): Promise<void> {
  const { error } = await supabase.rpc("job_connection_set_details", {
    p_connection_id: connectionId,
    p_state: patch.state ?? null,
    p_signal_label: patch.signal_label ?? null,
    p_cable_reference: patch.cable_reference ?? null,
    p_cable_length_m: patch.cable_length_m ?? null,
    p_cable_route: patch.cable_route ?? null,
    p_external_destination_type: patch.external_destination_type ?? null,
    p_notes: patch.notes ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function setPortRelevance(portId: number, relevance: string): Promise<void> {
  const { error } = await supabase.rpc("job_port_set_relevance", {
    p_port_id: portId,
    p_relevance: relevance,
  });
  if (error) throw new Error(error.message);
}

/**
 * Confirm every outstanding proposal in one room. Returns how many moved.
 * Only touches `auto_proposed` — a `needs_input` row is a question, and
 * accepting questions wholesale is answering them without reading them.
 */
export async function acceptRoomProposals(jobRoomId: number): Promise<number> {
  const { data, error } = await supabase.rpc("job_room_accept_proposals", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

/**
 * Whether a room has anything to draw, and whether what was produced from
 * the drawing is still true. The canvas itself re-renders from data every
 * time and so cannot be stale — a saved review or an issued PDF can.
 */
export interface RoomDrawingStatus {
  drawable_connections: number;
  total_connections: number;
  devices: number;
  nothing_to_draw: boolean;
  has_equipment: boolean;
  last_review_at: string | null;
  last_review_readiness: string | null;
  last_review_connections: number | null;
  review_is_stale: boolean;
}

export async function fetchRoomDrawingStatus(jobRoomId: number): Promise<RoomDrawingStatus> {
  const { data, error } = await supabase.rpc("job_room_drawing_status", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw new Error(error.message);
  return data as RoomDrawingStatus;
}

/**
 * Per-device state for one room: what the kit is, where it sits, and how
 * much of what the design asks of it is settled.
 *
 * `progress_label` is null — not "0 of 0" — when nothing is required of
 * the device, because "0 of 0" reads as an outstanding job and none
 * exists. `no_port_data` is the different problem: a device whose sockets
 * nobody has recorded cannot be scheduled at all.
 */
export interface RoomEquipmentSummaryRow {
  equipment_id: number;
  device_reference: string | null;
  description: string;
  manufacturer: string | null;
  model: string | null;
  category: string | null;
  equipment_role: string | null;
  schematic_zone: string | null;
  qty_required: number | null;
  image_url: string | null;
  ports_total: number;
  ports_required: number;
  ports_settled: number;
  progress_label: string | null;
  is_complete: boolean;
  /**
   * Ports were expected and none are recorded — a product nobody has
   * finished describing. False for accessories, which have none by nature.
   */
  no_port_data: boolean;
  /** False for the `accessory` category: mounts, brackets, cable. */
  expects_ports: boolean;
}

export async function fetchRoomEquipmentSummary(
  jobRoomId: number,
): Promise<RoomEquipmentSummaryRow[]> {
  const { data, error } = await supabase.rpc("job_room_equipment_summary", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as RoomEquipmentSummaryRow[];
}

/* ------------------------------------------------- rooms and locations */

/**
 * The physical layer beneath a quoted room (migrations 0226-0230).
 *
 * A bifold room is quoted as ONE room with all the kit under it and built
 * as two or more spaces with a moveable wall between them. The quote is
 * right; the schematic cannot use it, because a display "in Meeting Room"
 * is on one of two walls and a cable to it runs to one of two places.
 *
 * Read and written through the tables directly rather than an RPC: the
 * rules that matter are enforced by triggers and constraints, so there is
 * nothing for a function to add beyond a second place for them to drift.
 */

export interface RoomSpaceRow {
  id: number;
  job_id: number;
  job_room_id: number;
  position: number;
  /** Project-unique and permanent: R-001, R-002. Never reissued. */
  reference: string;
  name: string;
  has_credenza: boolean;
  has_equipment_behind_display: boolean;
  has_av_rack: boolean;
}

export interface FloorboxRow {
  id: number;
  space_id: number;
  position: number;
  name: string;
  enabled: boolean;
}

export interface EquipmentStoreRow {
  id: number;
  job_id: number;
  name: string;
}

const SPACE_COLUMNS =
  "id, job_id, job_room_id, position, reference, name, has_credenza, has_equipment_behind_display, has_av_rack";

export async function fetchRoomSpaces(jobRoomId: number): Promise<RoomSpaceRow[]> {
  const { data, error } = await supabase
    .from("job_room_spaces")
    .select(SPACE_COLUMNS)
    .eq("job_room_id", jobRoomId)
    .order("position");
  if (error) throw error;
  return (data ?? []) as RoomSpaceRow[];
}

/** Every space on the job, for the pickers that offer "which room?"
 * across a whole project rather than one quoted room at a time. */
export async function fetchJobSpaces(jobId: number): Promise<RoomSpaceRow[]> {
  const { data, error } = await supabase
    .from("job_room_spaces")
    .select(SPACE_COLUMNS)
    .eq("job_id", jobId)
    .order("reference");
  if (error) throw error;
  return (data ?? []) as RoomSpaceRow[];
}

export async function fetchFloorboxes(spaceIds: number[]): Promise<FloorboxRow[]> {
  if (spaceIds.length === 0) return [];
  const { data, error } = await supabase
    .from("job_room_floorboxes")
    .select("id, space_id, position, name, enabled")
    .in("space_id", spaceIds)
    .order("position");
  if (error) throw error;
  return (data ?? []) as FloorboxRow[];
}

export async function fetchEquipmentStores(jobId: number): Promise<EquipmentStoreRow[]> {
  const { data, error } = await supabase
    .from("job_equipment_stores")
    .select("id, job_id, name")
    .eq("job_id", jobId)
    .order("name");
  if (error) throw error;
  return (data ?? []) as EquipmentStoreRow[];
}

/** Adding a space fires the triggers from 0226: it takes the next R-00n
 * off the project's high-water mark and gets its five floorbox rows. The
 * reference and job_id are deliberately not sent -- they are the
 * database's to decide, and a client that guessed them would be the one
 * thing able to issue the same room number twice. */
export async function addRoomSpace(jobRoomId: number, position: number, name: string): Promise<RoomSpaceRow> {
  const { data, error } = await supabase
    .from("job_room_spaces")
    .insert({ job_room_id: jobRoomId, position, name })
    .select(SPACE_COLUMNS)
    .single();
  if (error) throw error;
  return data as RoomSpaceRow;
}

export interface RoomSpacePatch {
  name?: string;
  has_credenza?: boolean;
  has_equipment_behind_display?: boolean;
  has_av_rack?: boolean;
}

export async function updateRoomSpace(spaceId: number, patch: RoomSpacePatch): Promise<void> {
  const { error } = await supabase.from("job_room_spaces").update(patch).eq("id", spaceId);
  if (error) throw error;
}

/** Deleting a space leaves its reference retired. R-002 never comes back
 * as a different room, because a drawing issued a fortnight ago cites it. */
export async function deleteRoomSpace(spaceId: number): Promise<void> {
  const { error } = await supabase.from("job_room_spaces").delete().eq("id", spaceId);
  if (error) throw error;
}

export async function updateFloorbox(
  floorboxId: number,
  patch: { name?: string; enabled?: boolean },
): Promise<void> {
  const { error } = await supabase.from("job_room_floorboxes").update(patch).eq("id", floorboxId);
  if (error) throw error;
}

/** A room has no floorboxes until somebody adds one, and at most five.
 * The server picks the lowest free slot -- a room that has had one
 * removed fills the gap rather than running out of numbers. */
export async function addFloorbox(spaceId: number, name?: string): Promise<number> {
  const { data, error } = await supabase.rpc("job_space_add_floorbox", {
    p_space_id: spaceId,
    p_name: name ?? null,
  });
  if (error) throw error;
  return data as number;
}

/** Removing a floorbox unsets any device pointing at it (0231), so
 * nothing is left claiming a location that has gone. */
export async function deleteFloorbox(floorboxId: number): Promise<void> {
  const { error } = await supabase.from("job_room_floorboxes").delete().eq("id", floorboxId);
  if (error) throw error;
}

export async function addEquipmentStore(jobId: number, name: string): Promise<EquipmentStoreRow> {
  const { data, error } = await supabase
    .from("job_equipment_stores")
    .insert({ job_id: jobId, name })
    .select("id, job_id, name")
    .single();
  if (error) throw error;
  return data as EquipmentStoreRow;
}

export async function updateEquipmentStore(storeId: number, name: string): Promise<void> {
  const { error } = await supabase.from("job_equipment_stores").update({ name }).eq("id", storeId);
  if (error) throw error;
}

export async function deleteEquipmentStore(storeId: number): Promise<void> {
  const { error } = await supabase.from("job_equipment_stores").delete().eq("id", storeId);
  if (error) throw error;
}

/** How many devices sit in each space of a quoted room. Only used to say
 * what removing a room would cost: the foreign key is ON DELETE SET NULL,
 * so the devices survive but come back unplaced, and that is worth
 * telling somebody before they do it rather than after. */
export async function fetchSpaceDeviceCounts(jobRoomId: number): Promise<Map<number, number>> {
  const { data, error } = await supabase
    .from("job_equipment_requirements")
    .select("space_id")
    .eq("job_room_id", jobRoomId)
    .not("space_id", "is", null);
  if (error) throw error;
  const counts = new Map<number, number>();
  for (const row of (data ?? []) as { space_id: number }[]) {
    counts.set(row.space_id, (counts.get(row.space_id) ?? 0) + 1);
  }
  return counts;
}

/* ----------------------------------------------- device locations */

export interface DeviceLocationRow {
  equipment_id: number;
  space_id: number | null;
  equipment_store_id: number | null;
  location_type: string | null;
  floorbox_id: number | null;
  related_device_id: number | null;
  custom_location_text: string | null;
  /** Which named rack, when the location is a rack. From job_rack_items,
   * which holds at most one row per device. */
  rack_id: number | null;
  mount_method: string | null;
}

export async function fetchDeviceLocations(jobRoomId: number): Promise<DeviceLocationRow[]> {
  const { data, error } = await supabase
    .from("job_equipment_requirements")
    .select(
      "id, space_id, equipment_store_id, location_type, floorbox_id, related_device_id, custom_location_text, job_rack_items(rack_id, mount_method)",
    )
    .eq("job_room_id", jobRoomId);
  if (error) throw error;
  type Raw = Omit<DeviceLocationRow, "equipment_id" | "rack_id" | "mount_method"> & {
    id: number;
    job_rack_items: { rack_id: number; mount_method: string }[] | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((r) => {
    const item = r.job_rack_items?.[0] ?? null;
    return {
      equipment_id: r.id,
      space_id: r.space_id,
      equipment_store_id: r.equipment_store_id,
      location_type: r.location_type,
      floorbox_id: r.floorbox_id,
      related_device_id: r.related_device_id,
      custom_location_text: r.custom_location_text,
      rack_id: item?.rack_id ?? null,
      mount_method: item?.mount_method ?? null,
    };
  });
}

export interface DeviceLocationInput {
  spaceId: number | null;
  storeId: number | null;
  locationType: string | null;
  floorboxId: number | null;
  relatedDeviceId: number | null;
  customLocationText: string | null;
  rackId: number | null;
  mountMethod: string | null;
}

/**
 * Where a device is, in one call (migration 0236).
 *
 * One call rather than a field at a time, because a location is one
 * decision: setting the type without the floorbox, or moving a device out
 * of a rack while its rack assignment stays behind, are the half-states
 * that leave the schedule and the rack elevation disagreeing.
 */
export async function setDeviceLocation(
  equipmentId: number,
  loc: DeviceLocationInput,
): Promise<void> {
  const { error } = await supabase.rpc("job_equipment_set_location", {
    p_equipment_id: equipmentId,
    p_space_id: loc.spaceId,
    p_store_id: loc.storeId,
    p_location_type: loc.locationType,
    p_floorbox_id: loc.floorboxId,
    p_related_device_id: loc.relatedDeviceId,
    p_custom_location_text: loc.customLocationText,
    p_rack_id: loc.rackId,
    // Null, not a hardcoded "native_rackmount" -- the RPC itself now
    // decides that default from the product's own is_rack_mountable
    // (0531). A caller that DOES know the mount method (LocationPicker's
    // own explicit picker) still passes it through unchanged.
    p_mount_method: loc.mountMethod ?? null,
  });
  if (error) throw error;
}

export async function createRack(input: {
  jobId: number;
  label: string;
  spaceId: number | null;
  storeId: number | null;
  installationType: string | null;
  customInstallationText?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_rack_create", {
    p_job_id: input.jobId,
    p_label: input.label,
    p_space_id: input.spaceId,
    p_store_id: input.storeId,
    p_installation_type: input.installationType,
    p_custom_installation_text: input.customInstallationText ?? null,
  });
  if (error) throw error;
  return data as number;
}

/* ------------------------------------------------- the port board */

/**
 * Everything the room needs to show its equipment and connect its ports
 * (migration 0237). Nothing here is inferred: a port is connected only
 * when a connection record names it.
 */
export interface BoardPort {
  id: number;
  job_equipment_id: number;
  port_name: string;
  signal_type: string;
  direction: string;
  /** The physical plug -- what the socket looks like. */
  connector_key: string | null;
  /** What runs down the wire, which is what decides compatibility.
   * Null = unknown, and unknown joins nothing. */
  connector_family: string | null;
  allows_multiple_connections: boolean;
  is_nc: boolean;
  sort_order: number;
  /** Whether this port belongs on the signal schematic. Power is
   * materialised with this false (0187) so power runs do not clutter the
   * drawing -- which is not a reason to hide the socket from the person
   * standing in front of the box. */
  schematic_relevant: boolean;
  /** "pse" supplies PoE, "pd" draws it, null neither. This is what makes
   * a switch recognisable on the board without depending on a
   * Product Library role that is blank on plenty of real kit. */
  poe_role: string | null;
  /** lo_z / 70v / 100v -- set on an AMPLIFIER's own speaker-output port
   * (0478). Null means nobody has decided how this circuit is wired yet. */
  circuit_mode: string | null;
  /** The tap this SPEAKER's port is wired to, in 70v/100v mode (0478).
   * Meaningless in lo_z mode. */
  selected_tap_watts: number | null;
}

export interface BoardDevice {
  equipment_id: number;
  device_reference: string | null;
  description: string;
  manufacturer: string | null;
  model: string | null;
  space_id: number | null;
  equipment_store_id: number | null;
  location_type: string | null;
  floorbox_id: number | null;
  /** Which display this sits behind, when it sits behind one. */
  related_device_id: number | null;
  /** Where it is, in words, when "other" is the answer. */
  custom_location_text: string | null;
  /** From the product profile's equipment_role, the same source
   * app.is_display (0233) reads. Drives the placement order: other kit
   * can be put behind a display, so the display is placed first. */
  is_display: boolean;
  /** From equipment_role 'power_device' -- a rack PDU/power conditioner,
   * genuinely no data/AV ports of its own (Dale, live: "Pwr in
   * connections :/" -- a Citronic CPD-8C rack power conditioner showing
   * an empty "0 ports" card there). Unlike an accessory (0224/LCD
   * brackets), this still has its own real physical place -- a rack
   * position -- so it stays on Locations/Rack; only Connections, which
   * has nothing to offer a device with no ports to wire, hides it. */
  is_power_device: boolean;
  /** From equipment_role 'uc_compute' -- a Teams/Zoom Rooms compute puck
   * (0460: "an MCore, not a PC"). Drives Connections' prominent-ports
   * rule (0476): its LAN back to the network is easy to miss behind VCH,
   * the peripheral link everyone's actually looking at. */
  is_mcore: boolean;
  /** From equipment_role 'amplifier'/'speaker' (0478) -- which audio port
   * gets the Lo-Z/70V/100V circuit-mode control (the amp's) and which
   * gets the tap-wattage control (the speaker's). */
  is_amplifier: boolean;
  is_speaker: boolean;
  /** What kind of thing this is, from the Product Library's own category,
   * and whether it is the switch. Between them they head the list's
   * sub-groups -- displays, AV over IP, audio -- so a room of eighteen
   * boxes reads as five kinds rather than eighteen rows. */
  category: string | null;
  is_switch: boolean;
  /** attributes.is_managed_switch (0145) -- true/false only meaningful
   * alongside is_switch; null means the Product Library has never said. */
  is_managed_switch: boolean | null;
  /** Commissioned on the bench before it ships, rather than in the room.
   * A per-instance decision the designer makes here (0263), never
   * inferred from the product. */
  requires_offsite_commissioning: boolean;
  sort_order: number;
  ports: BoardPort[];
  /** The Product Library's own power_method (0454) -- 'unknown' is a
   * real, closed-set value (not a missing column), so "power supply
   * unknown -- set it" can offer the actual enum rather than free text. */
  power_method: string | null;
  /** From av_product_profiles.units_per_pack (0481/0485) -- 1 for most
   * products, >1 for something genuinely sold and delivered as a pack
   * (a stereo pair, a box of cable). Each unit is still its own device
   * row (job_split_equipment_units, 0481), so this only says how the
   * item was bought -- it is not a link to which other device it came
   * with. */
  units_per_pack: number;
  /** A quote line is not necessarily an independent physical AV device
   * (physicality.ts) -- a software licence, a bulk-materials allowance, a
   * client-supplied reference item. Rows classified into one of those
   * buckets never reach this array at all (fetchRoomPortBoard filters
   * them out below, the same way an accessory-category row always has);
   * this field is what survives that filter classifying itself, so
   * ConnectionsPanel can also suppress "Power unknown" for a physical
   * node whose own power question is meaningless (an assembly component,
   * a device already known to be passive). Null means nobody/nothing has
   * classified this row -- treated exactly like a genuinely unmatched
   * product always has been, not silently hidden. */
  physicality: EquipmentPhysicality | null;
}

export interface BoardConnection {
  id: number;
  from_port_id: number | null;
  to_port_id: number | null;
  from_equipment_id: number;
  to_equipment_id: number | null;
  from_port_name: string;
  to_port_name: string | null;
  /** Where a run ends when it does not end on another port in this room:
   * the client's LAN, today. Null for a normal port-to-port cable. */
  to_external_label: string | null;
  cable_reference: string | null;
}

export interface RoomPortBoard {
  devices: BoardDevice[];
  connections: BoardConnection[];
}

export async function fetchRoomPortBoard(jobRoomId: number): Promise<RoomPortBoard> {
  const { data, error } = await supabase
    .from("job_equipment_requirements")
    .select(
      "id, device_reference, description, model_no, space_id, equipment_store_id, location_type, floorbox_id, related_device_id, custom_location_text, requires_offsite_commissioning, sort_order, " +
        "manufacturers(name), av_product_profiles(model, equipment_role, category, rack_capacity_u, power_method, physicality, attributes, units_per_pack), " +
        "job_equipment_ports(id, job_equipment_id, port_name, signal_type, direction, connector_key, connector_family, allows_multiple_connections, is_nc, sort_order, schematic_relevant, poe_role, circuit_mode, selected_tap_watts)",
    )
    .eq("job_room_id", jobRoomId)
    .order("sort_order");
  if (error) throw error;

  type Raw = {
    id: number; device_reference: string | null; description: string; model_no: string | null;
    space_id: number | null; equipment_store_id: number | null; location_type: string | null;
    floorbox_id: number | null; related_device_id: number | null;
    custom_location_text: string | null;
    requires_offsite_commissioning: boolean | null;
    manufacturers: { name: string } | null;
    av_product_profiles: {
      model: string | null; equipment_role: string | null;
      category: string | null; rack_capacity_u: number | null;
      power_method: string | null; physicality: string | null;
      attributes: { is_managed_switch?: boolean } | null;
      units_per_pack: number | null;
    } | null;
    sort_order: number | null;
    job_equipment_ports: BoardPort[] | null;
  };

  const devices: BoardDevice[] = ((data ?? []) as unknown as Raw[])
    /* A RACK IS NOT A DEVICE ON THE PORT BOARD. It has no ports, nothing
       is patched into it, and it appeared here only because it is an
       equipment requirement like any other -- reading "0 ports", which
       looks like a Product Library gap rather than a cabinet. Where it
       belongs is the Rack tab. */
    .filter((r) => r.av_product_profiles?.rack_capacity_u == null)
    /* Same reasoning for an accessory (LCD bracket, trolley, faceplate,
       loose cable -- app.category_expects_ports() already treats the
       whole category this way, 0224): nothing is patched into a mount
       and it has no location decision of its own -- a bracket's "place"
       is wherever the display it carries goes. It cluttered both
       Locations and Connections reading "0 ports"/"no location" for
       something nobody would ever actually connect or site separately. */
    .filter((r) => r.av_product_profiles?.category !== "accessory")
    // A quote line is not necessarily an independent physical AV device
    // (physicality.ts): a software licence, a bulk-materials allowance, a
    // client-supplied reference item are all real BOM lines, none of them
    // placed/powered/wired like a genuine device. Classified once here,
    // off exactly the columns already selected above, and reused for both
    // the node-visibility filter and the physicality this device carries
    // onto the board (ConnectionsPanel reads it to gate "Power unknown").
    .map((r) => ({ r, physicality: classifyEquipmentPhysicality({
      description: r.description,
      category: r.av_product_profiles?.category ?? null,
      hasProfile: r.av_product_profiles != null,
      profilePhysicality: r.av_product_profiles?.physicality ?? null,
      powerMethod: r.av_product_profiles?.power_method ?? null,
    }) }))
    .filter(({ physicality }) => isSchematicNodePhysicality(physicality))
    .map(({ r, physicality }) => ({
    equipment_id: r.id,
    device_reference: r.device_reference,
    description: r.description,
    manufacturer: r.manufacturers?.name ?? null,
    model: r.av_product_profiles?.model ?? r.model_no,
    space_id: r.space_id,
    equipment_store_id: r.equipment_store_id,
    location_type: r.location_type,
    floorbox_id: r.floorbox_id,
    related_device_id: r.related_device_id,
    custom_location_text: r.custom_location_text,
    is_display: r.av_product_profiles?.equipment_role === "display",
    is_power_device: r.av_product_profiles?.equipment_role === "power_device",
    is_mcore: r.av_product_profiles?.equipment_role === "uc_compute",
    is_amplifier: r.av_product_profiles?.equipment_role === "amplifier",
    is_speaker: r.av_product_profiles?.equipment_role === "speaker",
    category: r.av_product_profiles?.category ?? null,
    is_switch: r.av_product_profiles?.equipment_role === "network_switch",
    is_managed_switch: r.av_product_profiles?.attributes?.is_managed_switch ?? null,
    requires_offsite_commissioning: r.requires_offsite_commissioning ?? false,
    sort_order: r.sort_order ?? 0,
    power_method: r.av_product_profiles?.power_method ?? null,
    // From av_product_profiles.units_per_pack (0481) -- shown as a "sold
    // as a pair"/"sold in packs of N" badge on Locations and Connections
    // (0485), same fact the quote Builder already badges. Each unit in
    // the pack is still its own separate device row here (0481's
    // job_split_equipment_units splits them out for wiring), so this is
    // a note about how the item was bought, not a link to a specific
    // sibling -- the schema has no group id that survives a quote line
    // ordering more than one pack, so this doesn't try to claim which
    // other device it's paired with.
    units_per_pack: r.av_product_profiles?.units_per_pack ?? 1,
    physicality,
    // EVERY port the box has, listed individually. The board used to drop
    // the ones that stay off the signal schematic and then count what was
    // left, so a device with eight ports offered to show seven and no
    // reader could tell that from a device that simply has seven. The
    // schematic's own filter still applies to the drawing; the list of
    // sockets in front of somebody is a different question.
    ports: (r.job_equipment_ports ?? [])
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.port_name.localeCompare(b.port_name)),
  }));

  const ids = devices.map((d) => d.equipment_id);
  if (ids.length === 0) return { devices, connections: [] };

  const { data: conns, error: cErr } = await supabase
    .from("job_equipment_connections")
    .select(
      "id, from_port_id, to_port_id, from_equipment_id, to_equipment_id, from_port_name, to_port_name, to_external_label, cable_reference",
    )
    .or(`from_equipment_id.in.(${ids.join(",")}),to_equipment_id.in.(${ids.join(",")})`);
  if (cErr) throw cErr;

  return { devices, connections: (conns ?? []) as BoardConnection[] };
}

/* --------------------------------------------------------------- cabling */

export interface RoomCable {
  id: number;
  cable_id: string;
  signal_family: string | null;
  cable_type: string | null;
  from_location: string | null;
  to_location: string | null;
  route_description: string | null;
  length_mode: string;
  standard_length_m: number | null;
  custom_length_m: number | null;
  effective_length_m: number | null;
  length_status: string;
  connection_id: number;
  /** What somebody buys, as against what the run carries. An RJ45 LED
   *  data cable is never described as a LAN patch lead. */
  cable_spec: string | null;
  connector_from: string | null;
  connector_to: string | null;
  colour: string | null;
  manufacturer: string | null;
  part_number: string | null;
  /** Only where a figure is actually published. Null means nobody has
   *  established one, which is said plainly rather than passed as fine. */
  max_distance_m: number | null;
  connection_role: string;
}

/** Every cable this room's confirmed connections created (0259).
 *
 * Through job_room_cables (0367) rather than the table, because a
 * cable's ends read the equipment's CURRENT location when nobody has
 * set one: nine rack patch leads were sitting under "Location not set"
 * while the kit at both ends had long since been put in RK6. */
export async function fetchRoomCables(jobRoomId: number): Promise<RoomCable[]> {
  const { data, error } = await supabase.rpc("job_room_cables", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw error;
  // Numerics arrive as strings over the wire; these are lengths, and a
  // length that is secretly a string sorts and sums like nonsense.
  return ((data ?? []) as unknown as RoomCable[]).map((r) => ({
    ...r,
    standard_length_m: r.standard_length_m == null ? null : Number(r.standard_length_m),
    custom_length_m: r.custom_length_m == null ? null : Number(r.custom_length_m),
    effective_length_m: r.effective_length_m == null ? null : Number(r.effective_length_m),
    max_distance_m: r.max_distance_m == null ? null : Number(r.max_distance_m),
  }));
}

/**
 * Sets the length on every cable of one run.
 *
 * Takes a list of ids rather than a route, because the route is a fact
 * the caller has already worked out and the database does not model it:
 * job_cables knows where each end is, not which runs share a journey.
 */
export async function setCableLengths(
  cableIds: number[],
  metres: number | null,
): Promise<void> {
  if (!cableIds.length) return;
  const patch = metres == null
    ? { length_mode: "not_set", standard_length_m: null, custom_length_m: null }
    : STANDARD_CABLE_LENGTHS.includes(metres)
      ? { length_mode: "standard", standard_length_m: metres, custom_length_m: null }
      : { length_mode: "custom", standard_length_m: null, custom_length_m: metres };
  const { error } = await supabase.from("job_cables").update(patch).in("id", cableIds);
  if (error) throw error;
}

/** Mirrors STANDARD_LENGTHS in cabling.ts -- kept here so the write path
 * decides "standard or custom" the same way the picker does. */
const STANDARD_CABLE_LENGTHS: number[] = [1, 2, 3, 5, 10, 15, 20, 25, 30];

export async function setCableLengthStatus(cableIds: number[], status: "estimate" | "confirmed"): Promise<void> {
  if (!cableIds.length) return;
  const { error } = await supabase.from("job_cables").update({ length_status: status }).in("id", cableIds);
  if (error) throw error;
}

/**
 * Whether this item is commissioned off site, before it ships (0263).
 *
 * A plain column update, the same way mtr_updated and firmware_updated
 * are written: there is nothing to decide beyond the tick, so there is
 * nothing for an RPC to enforce that the table's own policies do not.
 */
export async function setRequiresOffsiteCommissioning(
  equipmentId: number,
  required: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("job_equipment_requirements")
    .update({ requires_offsite_commissioning: required })
    .eq("id", equipmentId);
  if (error) throw error;
}

/**
 * One connection naming both ports (0237).
 *
 * Named apart from the older connectPorts, which goes through
 * job_schematic_connect_ports and can also point a port at an external
 * label like "Client LAN". This one joins two REAL ports and nothing
 * else, and the database refuses a second connection on an occupied
 * port, a port joined to itself, two ports on one device, mismatched
 * signals and mismatched directions.
 */
export async function connectPortPair(fromPortId: number, toPortId: number): Promise<number> {
  const { data, error } = await supabase.rpc("job_port_connect", {
    p_from_port_id: fromPortId,
    p_to_port_id: toPortId,
  });
  if (error) throw error;
  return data as number;
}

/** Setting either end back to "Not connected" removes the one connection
 * and frees both ports. Only correct for a port with AT MOST one
 * connection -- it deletes everything touching the port, which for an
 * allows_multiple_connections port (a daisy-chained speaker terminal or
 * Mic Pod, 0470/0476) would take down both links at once. Use
 * disconnectConnection below for those (0477). */
export async function disconnectPort(portId: number): Promise<void> {
  const { error } = await supabase.rpc("job_port_disconnect", { p_port_id: portId });
  if (error) throw error;
}

/** Removes exactly one connection by its own id (0477) -- the safe
 * disconnect for a port that can carry more than one link at once. */
export async function disconnectConnection(connectionId: number): Promise<void> {
  const { error } = await supabase.rpc("job_connection_disconnect", { p_connection_id: connectionId });
  if (error) throw error;
}

export type CircuitMode = "lo_z" | "70v" | "100v";

export interface SpeakerCircuitAmp {
  port_id: number;
  equipment_id: number;
  description: string;
  model_no: string | null;
  amp_min_impedance_ohms: number | null;
}

export interface SpeakerCircuitSpeaker {
  port_id: number;
  equipment_id: number;
  description: string;
  model_no: string | null;
  impedance_ohms: number | null;
  selected_tap_watts: number | null;
  /** From av_product_speaker_taps, for the circuit's declared voltage
   * system -- empty (not a fallback list) when the catalog has never
   * recorded this speaker's taps. */
  tap_options: number[];
}

export interface SpeakerCircuitCalc {
  type: "ohms" | "watts" | null;
  value: number | null;
  remaining_watts: number | null;
  compatible: boolean | null;
  issues: string[];
}

/** app.job_speaker_circuit_check (0478): the whole daisy chain reachable
 * from ANY port in it -- amp or speaker, doesn't matter which one is
 * passed -- with the Lo-Z/70V/100V arithmetic already done and every gap
 * (no impedance recorded, no tap set, no amp rating on file) named rather
 * than silently treated as zero. */
export async function speakerCircuitCheck(portId: number): Promise<{
  circuit_mode: CircuitMode | null;
  amp: SpeakerCircuitAmp | null;
  amp_rating_watts: number | null;
  speakers: SpeakerCircuitSpeaker[];
  calc: SpeakerCircuitCalc;
}> {
  const { data, error } = await supabase.rpc("job_speaker_circuit_check", { p_port_id: portId });
  if (error) throw error;
  return data as {
    circuit_mode: CircuitMode | null;
    amp: SpeakerCircuitAmp | null;
    amp_rating_watts: number | null;
    speakers: SpeakerCircuitSpeaker[];
    calc: SpeakerCircuitCalc;
  };
}

/** Set on the amplifier's own speaker-output port -- one circuit, one
 * mode; a daisy chain can't be half Lo-Z and half 70V. Null clears it
 * back to "nobody has decided yet". */
export async function setPortCircuitMode(portId: number, mode: CircuitMode | null): Promise<void> {
  const { error } = await supabase.rpc("job_port_set_circuit_mode", { p_port_id: portId, p_mode: mode });
  if (error) throw error;
}

/** Set on a SPEAKER's own port -- the tap it's wired to in 70v/100v
 * mode. Meaningless in lo_z mode, where impedance is what matters. */
export async function setPortTapWatts(portId: number, watts: number | null): Promise<void> {
  const { error } = await supabase.rpc("job_port_set_tap_watts", { p_port_id: portId, p_watts: watts });
  if (error) throw error;
}

/**
 * This port's run terminates on the client's network.
 *
 * One connection row with a from-port and no to-port: there is no device
 * at the far end to name, and inventing one would put equipment on the
 * drawing that nobody is supplying. `client_network` is a settled state,
 * not an outstanding one -- somebody else making the far end does not
 * leave our design unfinished.
 *
 * One RPC, not connect-then-set-state: those two are gated on different
 * permissions, so a can_manage_wip user could write the row and be
 * refused the state, leaving a run that reads "Confirmed" on a cable
 * nobody of ours is making.
 */
export async function connectPortToClientLan(portId: number): Promise<number> {
  const { data, error } = await supabase.rpc("job_port_connect_client_lan", {
    p_port_id: portId,
  });
  if (error) throw error;
  return data as number;
}

/** Runs a network port to the Viewpoint switch in its own room. The far
 * end is chosen server-side, because which port is right is a rule — a
 * PoE-drawing device needs a PoE-supplying port — and not a list for
 * somebody to scroll. */
export async function connectPortToRoomSwitch(portId: number): Promise<number> {
  const { data, error } = await supabase.rpc("job_port_connect_room_switch", {
    p_port_id: portId,
  });
  if (error) throw error;
  return data as number;
}

/**
 * The five kinds of port that can be added by hand, for kit the product
 * library does not describe. The kind sets the signal type, which is
 * what decides what may plug into it -- so this list is a rule, not a
 * menu, and its twin is the case statement in app.job_equipment_port_add
 * (0252).
 */
export const CUSTOM_PORT_KINDS = [
  { key: "hdmi", label: "HDMI", defaultName: "HDMI", direction: "output" },
  { key: "ethernet", label: "Ethernet", defaultName: "Ethernet", direction: "bidirectional" },
  { key: "usb", label: "USB", defaultName: "USB", direction: "bidirectional" },
  { key: "serial", label: "Serial", defaultName: "RS-232", direction: "bidirectional" },
  { key: "proprietary", label: "Proprietary", defaultName: "", direction: "bidirectional" },
] as const;

export type CustomPortKind = (typeof CUSTOM_PORT_KINDS)[number]["key"];

export async function addCustomPort(input: {
  equipmentId: number;
  kind: CustomPortKind;
  portName: string;
  direction: string;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_equipment_port_add", {
    p_equipment_id: input.equipmentId,
    p_kind: input.kind,
    p_port_name: input.portName,
    p_direction: input.direction,
  });
  if (error) throw error;
  return data as number;
}

/** N/C is a decision: this port is deliberately unused. Not the same as
 * nobody having got to it yet. */
export async function setPortNc(portId: number, isNc: boolean): Promise<void> {
  const { error } = await supabase.rpc("job_port_set_nc", { p_port_id: portId, p_is_nc: isNc });
  if (error) throw error;
}

/* ---------------------------------------------------------- 230V power
 *
 * The schedule is computed in the database (0267) so the drawing and the
 * page cannot disagree about the count. Everything here is transport:
 * fetch it, and record the answers somebody gives.
 *
 * Numerics arrive over PostgREST as strings, so anything reaching a
 * number input is coerced at this boundary rather than at the control.
 */

export interface PowerDevice {
  power_id: number | null;
  equipment_id: number;
  reference: string;
  description: string;
  manufacturer: string | null;
  model_no: string | null;
  units: number;
  method: string;
  inputs_each: number;
  mains_inputs: number;
  source: string;
  status: string;
  provision: string;
  pdu_id: number | null;
  pdu_name: string | null;
  outlets: Array<{ feed: number; pdu: string | null; outlet: number }>;
  notes: string | null;
  /** Where the SOCKET has to be, when that is not where the box is: a
   * screen on the wall plugs in behind itself. Null means "wherever the
   * equipment is", which is right nearly always.
   *
   * These come back so the editor can round-trip them. job_equipment_power_set
   * upserts all four from its arguments, so any save that dropped them
   * would silently erase the override. */
  power_location_type: string | null;
  power_floorbox_id: number | null;
  power_related_device_id: number | null;
  power_custom_location_text: string | null;
}

export interface PowerLocation {
  location: string;
  devices: PowerDevice[];
  local_outlets: number;
  pdu_outlets: number;
  /** What's actually installed at this physical place (job_location_
   * capacity, 0514) -- a count, not a source pick: a rack behind an
   * 8-way PDU only has one real socket in the wall; a table with loose
   * kit may have several. Saving these answers every still-unassigned
   * device there, consolidating onto a PDU where one covers it all. */
  installed_230v_sockets: number;
  /** Stored alongside the socket count (0514) but not yet wired to
   * anything -- Connections is its own per-port graph and needs its own
   * design pass before this number auto-connects a device. */
  installed_client_lan: number;
  /** How many client-LAN points this location actually needs, computed
   * from the room's own connection graph (job_power_schedule, 0541): one
   * per connection leaving the documented topology for the client's own
   * network (external_destination_type = 'client_network'), so N devices
   * behind one switch's uplink read as 1 required, not N. Compared
   * against installed_client_lan above, never summed with it. */
  client_lan_required: number;
}

export interface PowerPdu {
  id: number;
  name: string;
  /** A label only (0512) -- an extension lead and a PDU are counted
   * identically; this just says which word the schedule should use. */
  kind: "pdu" | "extension_lead";
  outlet_quantity: number;
  occupied: number;
  spare: number;
  upstream_feed_quantity: number;
  upstream_feed_type: string;
  /** Whether this PDU's own building feed already exists or still needs
   * running (0378) -- independent of how many outlets it has occupied.
   * Meaningless (and not shown) once upstream_pdu_id is set (0512) --
   * its one feed is an occupied outlet upstream, not a building supply. */
  upstream_feed_status: "existing" | "new";
  rack_id: number | null;
  /** Set when this PDU/lead's own single feed comes from another PDU's
   * outlet rather than the building (0512) -- a 4-way plugged into a
   * rack PDU rather than the wall. */
  upstream_pdu_id: number | null;
  upstream_pdu_name: string | null;
  /** Linked Product Library item (0513) -- what makes this PDU/lead a
   * real orderable line on Kit Balance/the Equipment tab, not just
   * power-schedule bookkeeping. Null: noted as needed, nothing bought
   * yet -- same optional-until-specified shape a rack PDU already has
   * (job_rack_pdus.job_equipment_id, 0453). */
  job_equipment_id: number | null;
  in_use: boolean;
  notes: string | null;
}

export interface PowerTotals {
  local_outlets: number;
  pdu_outlets: number;
  upstream: number;
  hardwired: number;
  existing: number;
  unassigned: number;
  poe_devices: number;
  unknown_devices: number;
  /** Equipment instances needing mains, never ports or inputs (0378). */
  mains_equipment?: number;
  /** The new-status share of `upstream` (0378) -- already folded into
   * `new_outlets_required` below; kept separately for display. */
  upstream_new?: number;
  /** The existing-status share of `upstream` (0378). */
  upstream_existing?: number;
  new_provisions?: number;
  new_outlets_required?: number;
  /** Direct existing-outlet devices plus existing-status PDU feeds (0378)
   * -- the building-socket hierarchy's other half from new_outlets_required. */
  existing_outlets?: number;
  connections: number;
  /** Client-LAN points required across this scope (room or job), summed
   * from each location's own client_lan_required (0541). */
  client_lan_required?: number;
}

export interface PowerRoom {
  job_room_id: number;
  room_name: string;
  locations: PowerLocation[];
  pdus: PowerPdu[];
  totals: PowerTotals;
}

export interface PowerSchedule {
  job_id: number;
  generated_at: string;
  spare: { mode: string; custom: number | null };
  rooms: PowerRoom[];
  unroomed: PowerLocation[];
  unknown_equipment: Array<{
    equipment_id: number;
    reference: string;
    description: string;
    job_room_id: number | null;
    units: number;
    /** The Product Library's own category (0363) -- what lets the list
     * be grouped by kind instead of running flat. Null where the item
     * has no linked profile. */
    category: string | null;
  }>;
  totals: PowerTotals;
}

const pnum = (v: unknown): number => (v == null ? 0 : Number(v));

function coerceDevice(d: PowerDevice): PowerDevice {
  return {
    ...d,
    units: pnum(d.units),
    inputs_each: pnum(d.inputs_each),
    mains_inputs: pnum(d.mains_inputs),
    outlets: d.outlets ?? [],
  };
}

function coerceLocation(l: PowerLocation): PowerLocation {
  return {
    ...l,
    devices: (l.devices ?? []).map(coerceDevice),
    local_outlets: pnum(l.local_outlets),
    pdu_outlets: pnum(l.pdu_outlets),
    installed_230v_sockets: pnum(l.installed_230v_sockets),
    installed_client_lan: pnum(l.installed_client_lan),
    client_lan_required: pnum(l.client_lan_required),
  };
}

export async function fetchPowerSchedule(jobId: number): Promise<PowerSchedule> {
  const { data, error } = await supabase.rpc("job_power_schedule", { p_job_id: jobId });
  if (error) throw error;
  const raw = data as PowerSchedule;
  return {
    ...raw,
    rooms: (raw.rooms ?? []).map((r) => ({
      ...r,
      locations: (r.locations ?? []).map(coerceLocation),
      pdus: (r.pdus ?? []).map((p) => ({
        ...p,
        kind: p.kind ?? "pdu",
        outlet_quantity: pnum(p.outlet_quantity),
        occupied: pnum(p.occupied),
        spare: pnum(p.spare),
        upstream_feed_quantity: pnum(p.upstream_feed_quantity),
        upstream_feed_status: p.upstream_feed_status ?? "new",
      })),
    })),
    unroomed: (raw.unroomed ?? []).map(coerceLocation),
    unknown_equipment: raw.unknown_equipment ?? [],
    spare: { mode: raw.spare?.mode ?? "none", custom: raw.spare?.custom == null ? null : Number(raw.spare.custom) },
  };
}

export interface PoeLink {
  equipment_id: number;
  reference: string;
  units: number;
  method: string;
  class_watts: number | null;
  device_port: string | null;
  switch_id: number | null;
  switch_reference: string | null;
  switch_port: string | null;
  port_sufficient: boolean | null;
}

export interface PoeSchedule {
  links: PoeLink[];
  switches: Array<{
    switch_id: number;
    reference: string | null;
    demand_watts: number | null;
    budget_watts: number | null;
    budget_sufficient: boolean | null;
  }>;
  unfed: Array<{ equipment_id: number; reference: string }>;
}

export async function fetchPoeSchedule(jobId: number): Promise<PoeSchedule> {
  const { data, error } = await supabase.rpc("job_poe_schedule", { p_job_id: jobId });
  if (error) throw error;
  const raw = data as PoeSchedule;
  return {
    links: (raw.links ?? []).map((l) => ({
      ...l,
      units: pnum(l.units),
      class_watts: l.class_watts == null ? null : Number(l.class_watts),
    })),
    switches: (raw.switches ?? []).map((s) => ({
      ...s,
      demand_watts: s.demand_watts == null ? null : Number(s.demand_watts),
      budget_watts: s.budget_watts == null ? null : Number(s.budget_watts),
    })),
    unfed: raw.unfed ?? [],
  };
}

/**
 * The RPC upserts every column it is given, including the four socket-location
 * ones -- so this is a whole-row write, not a patch. Callers must pass the
 * device's current values for everything they are not changing; PowerPanel
 * builds them from the row it is showing rather than from memory.
 */
export async function setEquipmentPower(input: {
  equipmentId: number;
  method: string;
  inputsPerUnit: number;
  source: string;
  status: string;
  pduId?: number | null;
  locationType?: string | null;
  floorboxId?: number | null;
  relatedDeviceId?: number | null;
  customLocationText?: string | null;
  notes?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_equipment_power_set", {
    p_equipment_id: input.equipmentId,
    p_power_method: input.method,
    p_mains_inputs_per_unit: input.inputsPerUnit,
    p_power_source: input.source,
    p_status: input.status,
    p_pdu_id: input.pduId ?? null,
    p_power_location_type: input.locationType ?? null,
    p_power_floorbox_id: input.floorboxId ?? null,
    p_power_related_device_id: input.relatedDeviceId ?? null,
    p_power_custom_location_text: input.customLocationText ?? null,
    p_notes: input.notes ?? null,
  });
  if (error) throw error;
  return data as number;
}

/** Fill in power for every device still marked unknown, from what the
 * Product Library records: a 230V inlet count means mains, a PD port
 * means PoE, and a profile that says neither leaves the row unknown.
 * Rows somebody already classified are never touched. */
export async function classifyPowerFromLibrary(
  jobId: number,
): Promise<{ mains: number; poe: number; still_unknown: number }> {
  const { data, error } = await supabase.rpc("job_power_classify_from_library", {
    p_job_id: jobId,
  });
  if (error) throw error;
  return data as { mains: number; poe: number; still_unknown: number };
}

export async function upsertPdu(input: {
  id?: number | null;
  jobId: number;
  jobRoomId: number | null;
  /** Which rack this PDU physically lives in and feeds, if any -- the
   * RPC (and job_power_schedule's own JSON) has carried this since 0267,
   * but nothing on the client ever passed anything but null, so a rack
   * full of kit had no way to read as "one PDU's outlets", only as N
   * individual "no source chosen" devices each demanding their own
   * building feed. Null: an ordinary room PDU, unrelated to any rack. */
  rackId?: number | null;
  name: string;
  outletQuantity: number;
  upstreamFeedQuantity: number;
  upstreamFeedType: string;
  /** Existing/new for the PDU's own building feed (0378). Omit on an
   * update to leave it as-is; a new PDU defaults to "new" server-side.
   * Ignored once upstreamPduId is set (0512). */
  upstreamFeedStatus?: "existing" | "new";
  /** Label only (0512) -- omit on an update to leave it as-is; a new
   * PDU defaults to "pdu" server-side. */
  kind?: "pdu" | "extension_lead";
  /** This PDU/lead's own single feed comes from another PDU's outlet
   * instead of the building (0512). Null: feeds from the building,
   * same as every PDU before this existed. */
  upstreamPduId?: number | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_pdu_upsert", {
    p_id: input.id ?? null,
    p_job_id: input.jobId,
    p_job_room_id: input.jobRoomId,
    p_rack_id: input.rackId ?? null,
    p_name: input.name,
    p_outlet_quantity: input.outletQuantity,
    p_upstream_feed_quantity: input.upstreamFeedQuantity,
    p_upstream_feed_type: input.upstreamFeedType,
    p_notes: null,
    p_upstream_feed_status: input.upstreamFeedStatus ?? null,
    p_kind: input.kind ?? null,
    p_upstream_pdu_id: input.upstreamPduId ?? null,
  });
  if (error) throw error;
  return data as number;
}

export async function deletePdu(id: number): Promise<void> {
  const { error } = await supabase.rpc("job_pdu_delete", { p_id: id });
  if (error) throw error;
}

/** One rack's own PDU(s) -- the same job_pdus rows PowerPanel's whole-job
 * schedule already carries, just scoped to a single rack (0540) so the
 * rack elevation screen doesn't need to pull a whole job's power schedule
 * just to show what's feeding this one rack. Every rack now gets one of
 * these automatically (job_rack_ensure_pdu, 0540) -- this is how
 * RackDetail's "Power distribution" card sees it. */
export type RackPdu = {
  id: number;
  name: string;
  kind: "pdu" | "extension_lead";
  outlet_quantity: number;
  occupied: number;
  upstream_feed_quantity: number;
  upstream_feed_type: string;
  upstream_feed_status: "existing" | "new";
  job_equipment_id: number | null;
  model: string | null;
  notes: string | null;
};

export async function fetchRackPdus(rackId: number): Promise<RackPdu[]> {
  const { data, error } = await supabase.rpc("job_rack_pdus_list", { p_rack_id: rackId });
  if (error) throw error;
  return (data as RackPdu[] | null) ?? [];
}

/** What's installed at one physical location -- a count of real 230V
 * sockets and client LAN points, not a source pick (job_location_
 * capacity_set, 0514). Saving the socket count answers every still-
 * unassigned mains device there: onto a PDU already covering the whole
 * location if one does (only that PDU's own building feed is then
 * needed, per Dale -- "if in rack pdu outlets >= required 230v sockets
 * the pdu only needs one socket"), otherwise directly, one device at a
 * time, until the count runs out. */
export async function setLocationCapacity(input: {
  jobRoomId: number;
  location: string;
  installed230vSockets: number;
  installedClientLan: number;
}): Promise<{ sockets_needed?: number; pdu_id?: number | null; assigned_to_pdu: number; assigned_local: number }> {
  const { data, error } = await supabase.rpc("job_location_capacity_set", {
    p_job_room_id: input.jobRoomId,
    p_location: input.location,
    p_installed_230v_sockets: input.installed230vSockets,
    p_installed_client_lan: input.installedClientLan,
  });
  if (error) throw error;
  return data as { sockets_needed?: number; pdu_id?: number | null; assigned_to_pdu: number; assigned_local: number };
}

/** Links (or, with null, unlinks) a power-schedule PDU/extension lead to
 * a real Product Library item (0513) -- what makes it a real orderable
 * line on Kit Balance/the Equipment tab, for a room-level PDU and for a
 * rack's own PDU alike (0540 -- a rack's PDU is a job_pdus row too).
 * Unlinking never deletes the equipment row it already created. */
export async function setPduProduct(pduId: number, avProductProfileId: number | null): Promise<number | null> {
  const { data, error } = await supabase.rpc("job_pdu_set_product", {
    p_pdu_id: pduId,
    p_av_product_profile_id: avProductProfileId,
  });
  if (error) throw error;
  return (data as number | null) ?? null;
}

/** One socket, one feed. The database refuses a second device on the
 *  same outlet; this just carries the refusal back. */
export async function assignPduOutlet(powerId: number, pduId: number, outlet: number): Promise<void> {
  const { error } = await supabase.rpc("job_power_assign_outlet", {
    p_power_id: powerId,
    p_pdu_id: pduId,
    p_outlet_number: outlet,
    p_feed_index: 1,
  });
  if (error) throw error;
}

export async function clearPduOutlet(powerId: number): Promise<void> {
  const { error } = await supabase.rpc("job_power_clear_outlet", {
    p_power_id: powerId,
    p_feed_index: 1,
  });
  if (error) throw error;
}

export async function setPowerSpare(jobId: number, mode: string, custom: number | null): Promise<void> {
  const { error } = await supabase.rpc("job_set_power_spare", {
    p_job_id: jobId,
    p_mode: mode,
    p_custom: custom,
  });
  if (error) throw error;
}

/* ------------------------------------------------------ LED walls (0271)
 *
 * A wall is ONE piece of equipment however many cabinets it is built
 * from, with as many LED DATA INPUTS as the design needs. led_data is
 * its own signal family, so the ordinary port board will not offer one
 * of these to the client LAN or a network switch -- that rule lives in
 * the database and needs nothing here.
 */

export interface LedPort {
  id: number;
  name: string;
  direction: string;
  connector_key: string | null;
  section: string | null;
  connected_to: string | null;
  cable_id: string | null;
}

export interface LedConfig {
  id: number;
  pixel_pitch_mm: number | null;
  physical_width_mm: number | null;
  physical_height_mm: number | null;
  pixel_width: number | null;
  pixel_height: number | null;
  brightness_nits: number | null;
  cabinet_model: string | null;
  cabinet_quantity: number | null;
  environment: string | null;
  installation_type: string | null;
  notes: string | null;
}

export interface LedDevice {
  equipment_id: number;
  reference: string;
  description: string;
  manufacturer: string | null;
  model_no: string | null;
  role: string | null;
  config: LedConfig | null;
  sections: Array<{ id: number; name: string }>;
  ports: LedPort[];
}

export async function fetchLedDevices(jobRoomId: number): Promise<LedDevice[]> {
  const { data, error } = await supabase.rpc("job_room_led_devices", {
    p_job_room_id: jobRoomId,
  });
  if (error) throw error;
  return ((data ?? []) as LedDevice[]).map((d) => ({
    ...d,
    config: d.config
      ? {
          ...d.config,
          pixel_pitch_mm: d.config.pixel_pitch_mm == null ? null : Number(d.config.pixel_pitch_mm),
          physical_width_mm:
            d.config.physical_width_mm == null ? null : Number(d.config.physical_width_mm),
          physical_height_mm:
            d.config.physical_height_mm == null ? null : Number(d.config.physical_height_mm),
        }
      : null,
    sections: d.sections ?? [],
    ports: d.ports ?? [],
  }));
}

export async function createLedDisplay(input: {
  jobId: number;
  jobRoomId: number;
  manufacturer: string;
  modelReference: string;
  description: string;
  deviceReference: string | null;
  sections: string[];
  inputCount: number;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_led_display_create", {
    p_job_id: input.jobId,
    p_job_room_id: input.jobRoomId,
    p_manufacturer: input.manufacturer,
    p_model_reference: input.modelReference,
    p_description: input.description,
    p_device_reference: input.deviceReference,
    // Named sections decide the input count when there are any: one feed
    // per section is what somebody actually asked for.
    p_sections: input.sections.length > 0 ? input.sections : null,
    p_input_count: input.inputCount,
  });
  if (error) throw error;
  return data as number;
}

export async function createVideowallProcessor(input: {
  jobId: number;
  jobRoomId: number;
  manufacturer: string;
  model: string;
  description: string;
  deviceReference: string | null;
  outputCount: number;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_equipment_add_custom", {
    p_job_id: input.jobId,
    p_job_room_id: input.jobRoomId,
    p_category: "videowall_processor",
    p_role: "videowall_processor",
    p_manufacturer: input.manufacturer,
    p_model_no: input.model,
    p_description: input.description,
    p_device_reference: input.deviceReference,
  });
  if (error) throw error;
  const equipmentId = data as number;
  for (let i = 1; i <= input.outputCount; i++) {
    await addLedPort({
      equipmentId,
      direction: "output",
      name: `LED Data Output ${i}`,
      connectorKey: null,
      sectionId: null,
    });
  }
  return equipmentId;
}

export async function addLedPort(input: {
  equipmentId: number;
  direction: string;
  name: string;
  connectorKey: string | null;
  sectionId: number | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("job_led_port_add", {
    p_equipment_id: input.equipmentId,
    p_direction: input.direction,
    p_port_name: input.name,
    p_connector_key: input.connectorKey,
    p_led_section_id: input.sectionId,
  });
  if (error) throw error;
  return data as number;
}

/** Refused while a cable lands on it -- the connection is the thing to
 *  reconsider first, and the database says so. */
export async function removeLedPort(portId: number): Promise<void> {
  const { error } = await supabase.rpc("job_led_port_remove", { p_port_id: portId });
  if (error) throw error;
}

export async function setLedConfiguration(
  equipmentId: number,
  payload: Record<string, string | null>,
): Promise<void> {
  const { error } = await supabase.rpc("job_led_configuration_set", {
    p_job_equipment_id: equipmentId,
    p_payload: payload,
  });
  if (error) throw error;
}

export async function addLedSection(configId: number, name: string): Promise<number> {
  const { data, error } = await supabase.rpc("job_led_section_add", {
    p_config_id: configId,
    p_name: name,
  });
  if (error) throw error;
  return data as number;
}

/** What a cable is made of, what is on each end, and how far it is
 *  verified to reach. Nothing here is invented: a part number nobody has
 *  chosen stays blank rather than becoming a plausible-looking SKU. */
export async function setCableSpec(input: {
  cableId: number;
  cableSpec: string | null;
  connectorFrom: string | null;
  connectorTo: string | null;
  colour: string | null;
  manufacturer: string | null;
  partNumber: string | null;
  maxDistanceM: number | null;
  role: string | null;
  notes: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("job_cable_set_spec", {
    p_cable_id: input.cableId,
    p_cable_spec: input.cableSpec,
    p_connector_from: input.connectorFrom,
    p_connector_to: input.connectorTo,
    p_colour: input.colour,
    p_manufacturer: input.manufacturer,
    p_part_number: input.partNumber,
    p_max_distance_m: input.maxDistanceM,
    p_connection_role: input.role,
    p_notes: input.notes,
  });
  if (error) throw error;
}

/* ------------------------------------------------- rack suggester (0278) */

/** One rack the catalogue can actually supply. Every field except the
 * identity ones is nullable because the price list is the only source and
 * it does not state everything -- a rack whose depth RGB never printed
 * comes back with depth_mm null rather than a guess. */
export interface RackSuggestion {
  profile_id: number;
  manufacturer: string | null;
  model: string | null;
  description: string | null;
  rack_u: number;
  depth_mm: number | null;
  mounting: string | null;
  front_door: string | null;
  is_clearance: boolean;
  supplier: string | null;
  supplier_sku: string | null;
  cost: number | null;
  list_price: number | null;
  priced_on: string | null;
  /** rack_u minus the requirement inflated by spare_pct -- how much room
   * is left after the spare allowance is already taken. */
  spare_u: number;
}

export interface PduSuggestion {
  profile_id: number;
  manufacturer: string | null;
  model: string | null;
  description: string | null;
  outlets: number;
  outlet_type: string | null;
  supplier: string | null;
  supplier_sku: string | null;
  cost: number | null;
  list_price: number | null;
}

export interface RackSuggestionResult {
  required_u: number;
  spare_pct: number;
  effective_u: number;
  mounting: string | null;
  min_depth_mm: number | null;
  required_outlets: number | null;
  racks: RackSuggestion[];
  pdus: PduSuggestion[];
}

/**
 * "Which rack should we buy for this?" -- a pure catalogue query, asked
 * only when somebody presses the button. It reads nothing about the job,
 * writes nothing, and proposes nothing on its own: the requirement comes
 * in as numbers the caller already has on screen, and what comes back is
 * a list of orderable racks, not a decision.
 *
 * requiredOutlets is optional because the PDU half of the answer is
 * optional -- pass null and the pdus array comes back empty rather than
 * filled with whatever happened to be in the catalogue.
 */
export async function fetchRackSuggestions(input: {
  requiredU: number;
  requiredOutlets?: number | null;
  mounting?: string | null;
  minDepthMm?: number | null;
  sparePct?: number;
  limit?: number;
}): Promise<RackSuggestionResult> {
  const { data, error } = await supabase.rpc("rack_suggest", {
    p_required_u: input.requiredU,
    p_required_outlets: input.requiredOutlets ?? null,
    p_mounting: input.mounting ?? null,
    p_min_depth_mm: input.minDepthMm ?? null,
    p_spare_pct: input.sparePct ?? 20,
    p_limit: input.limit ?? 8,
  });
  if (error) throw error;
  return data as RackSuggestionResult;
}

/* ===== Kit Balance (commercial Stage 3) =====

   What the job was sold as, reconciled against what it actually holds.
   Read-only, and gated server-side on project commercials -- a caller
   without it gets a 403 rather than a smaller answer, so the tab is
   hidden rather than degraded. */

export type KitBalanceStatus =
  | "matched"
  | "quantity_changed"
  | "product_changed"
  | "omitted"
  | "kit_decomposed"
  | "optional_not_taken";

export interface KitBalanceLine {
  status: KitBalanceStatus;
  quote_line_item_id: number | null;
  description: string | null;
  model_no: string | null;
  supplier: string | null;
  part_code: string | null;
  subsection: string | null;
  is_optional: boolean;
  baseline_qty: number;
  baseline_unit_cost: number;
  baseline_unit_sell: number;
  baseline_cost: number;
  baseline_sell: number;
  live_qty: number;
  live_cost: number;
  live_line_count: number;
  live_models: string[];
  /** Kits report in kits, not boxes: each of the x components carries
   * 1/x, capped at its own share, so a complete kit balances against
   * the one that was sold and a short one shows the fraction. Null on
   * everything that is not a kit. */
  kit_components_expected: number | null;
  kit_components_present: number | null;
  kit_complete: boolean;
  /** False where the live rows carry no cost at all -- a kit's
   * components, or a line nobody has costed yet. Such a line is held
   * out of the observed variance rather than booked as a saving. */
  cost_comparable: boolean;
  qty_variance: number;
  cost_variance: number;
  event_count: number;
  approved_gp_impact: number;
  pending_gp_impact: number;
  /** Moved, with nothing in the change ledger to explain it. The
   * reason this tab exists. */
  unreconciled: boolean;
  procurement: { ordered: number; received: number; delivered: number };
}

export interface KitBalanceAddedLine {
  status: "added";
  description: string | null;
  model_no: string | null;
  supplier: string | null;
  live_line_count: number;
  live_qty: number;
  live_cost: number;
}

export interface KitBalance {
  job_id: number;
  has_baseline: boolean;
  baseline: {
    original_contract_value: number | null;
    original_budget_cost: number | null;
    original_gp: number | null;
    original_margin_pct: number | null;
    captured_at: string | null;
    source_quote_revision: number | null;
  } | null;
  totals: {
    baseline_cost: number;
    baseline_sell: number;
    optional_cost: number;
    live_cost: number;
    /** What IS, from comparing baseline to live. Never added to the
     * ledger figures -- they are different kinds of fact. */
    observed_cost_variance: number;
    cost_not_comparable: number;
    counts: {
      matched: number;
      quantity_changed: number;
      product_changed: number;
      omitted: number;
      kit_decomposed: number;
      optional_not_taken: number;
      added: number;
      kit_incomplete: number;
      unreconciled: number;
    };
  };
  /** What has been AGREED, from the append-only change ledger. */
  ledger: {
    event_count: number;
    approved_cost: number;
    approved_revenue: number;
    approved_gp: number;
    pending_cost: number;
    pending_revenue: number;
    pending_gp: number;
  };
  lines: KitBalanceLine[];
  added_lines: KitBalanceAddedLine[];
}

export async function fetchKitBalance(jobId: number): Promise<KitBalance> {
  const { data, error } = await supabase.rpc("job_kit_balance", { p_job_id: jobId });
  if (error) throw error;
  return data as KitBalance;
}

/* ===== Labour: plan against days worked (commercial Stage 4) =====

   The PM schedules the work in the Planner; a worked day is CONFIRMED
   from that schedule rather than typed a second time. Manual entry
   stays available for a day nobody planned. */

export interface LabourStage {
  work_stage_id: number;
  stage_key: string;
  stage_label: string;
  planned_days: number;
  actual_days: number;
  approved_days: number;
  people: number;
  days_variance: number;
  /** Null where the zone rate card has no rate for this stage -- and
   * "Other" never has one. Null means unknown, never zero. */
  cost_rate: number | null;
  sell_rate: number | null;
  planned_cost: number | null;
  actual_cost: number | null;
  cost_variance: number | null;
}

export interface LabourBalance {
  job_id: number;
  zone: { id: number; name: string } | null;
  /** False until the quote picks a labour zone. Every cost figure is
   * null while this is false; the days are still exact. */
  has_rates: boolean;
  stages: LabourStage[];
  totals: {
    planned_days: number;
    actual_days: number;
    approved_days: number;
    days_variance: number;
    planned_cost: number | null;
    actual_cost: number | null;
    cost_variance: number | null;
    days_without_rate: number;
  };
  by_person: {
    profile_id: string;
    person: string;
    days: number;
    approved_days: number;
    first_day: string;
    last_day: string;
  }[];
  /** Days a person has committed past 1.0 across every job, not just
   * this one. Reported, never blocked. */
  overcommitted_days: {
    profile_id: string;
    person: string;
    entry_date: string;
    total_fraction: number;
  }[];
}

export interface ScheduledDay {
  assignment_id: number;
  assignment_date: string;
  profile_id: string;
  person: string;
  work_stage_id: number;
  stage_label: string | null;
  day_fraction: number;
  notes: string | null;
  recorded: boolean;
  approved: boolean;
}

export async function fetchLabourBalance(jobId: number): Promise<LabourBalance> {
  const { data, error } = await supabase.rpc("job_labour_balance", { p_job_id: jobId });
  if (error) throw error;
  return data as LabourBalance;
}

export async function fetchScheduledDays(jobId: number): Promise<ScheduledDay[]> {
  const { data, error } = await supabase.rpc("job_scheduled_days", { p_job_id: jobId });
  if (error) throw error;
  return (data ?? []) as ScheduledDay[];
}

/** Idempotent: pressing it twice creates nothing the second time and
 * says so, rather than looking like it failed. */
export async function confirmScheduledDays(
  jobId: number,
  assignmentIds: number[],
): Promise<{ created: number; already_confirmed: number }> {
  const { data, error } = await supabase.rpc("job_confirm_scheduled_days", {
    p_job_id: jobId,
    p_assignment_ids: assignmentIds,
  });
  if (error) throw error;
  return data as { created: number; already_confirmed: number };
}

export interface TimeEntry {
  id: number;
  job_id: number;
  profile_id: string;
  work_stage_id: number;
  entry_date: string;
  day_fraction: number;
  notes: string | null;
  source: "manual" | "assignment";
  approved_at: string | null;
  approved_by: string | null;
}

export async function fetchTimeEntries(jobId: number): Promise<TimeEntry[]> {
  const { data, error } = await supabase
    .from("project_time_entries")
    .select("id, job_id, profile_id, work_stage_id, entry_date, day_fraction, notes, source, approved_at, approved_by")
    .eq("job_id", jobId)
    .order("entry_date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as TimeEntry[];
}

export async function createTimeEntry(input: {
  job_id: number;
  profile_id: string;
  work_stage_id: number;
  entry_date: string;
  day_fraction: number;
  notes?: string | null;
}): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase.from("project_time_entries").insert({
    ...input,
    source: "manual",
    created_by: auth.user?.id,
  });
  if (error) throw error;
}

export async function deleteTimeEntry(id: number): Promise<void> {
  const { error } = await supabase.from("project_time_entries").delete().eq("id", id);
  if (error) throw error;
}

/** Approving needs can_approve_project_commercials; the database
 * refuses anyone else, so the button is hidden rather than failing. */
export async function setTimeEntryApproved(id: number, approved: boolean): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("project_time_entries")
    .update(
      approved
        ? { approved_by: auth.user?.id, approved_at: new Date().toISOString() }
        : { approved_by: null, approved_at: null },
    )
    .eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------- commercial closure (0373) */

export interface JobForecast {
  job_id: number;
  has_baseline: boolean;
  revenue?: {
    sold: number; approved_change: number; pending_change: number;
    forecast: number; invoiced_to_date: number;
  };
  bought?: {
    sold_plan: number; approved_change: number; plan: number;
    to_date: number; to_complete: number; forecast: number;
    against_changes: number; ledger_lines: number; from_matching: number;
  };
  labour?: {
    visible: boolean; sold_cost: number | null; budget_cost: number | null;
    worked_cost: number | null; forecast: number | null; has_rates: boolean;
    planned_days: number | null; actual_days: number | null;
  };
  profit?: {
    sold_cost: number; sold_gp: number; sold_margin_pct: number | null;
    forecast_cost: number; forecast_gp: number; forecast_margin_pct: number | null;
    movement: number;
  };
  attention?: string[];
}

export async function fetchJobForecast(jobId: number): Promise<JobForecast> {
  const { data, error } = await supabase.rpc("job_forecast", { p_job_id: jobId });
  if (error) throw error;
  return data as JobForecast;
}

export interface CommercialRisk {
  key: string;
  severity: "amber" | "red";
  /** Which tab the underlying record lives on. */
  link: string;
  title: string;
  detail: string;
}

export async function fetchCommercialRisks(jobId: number): Promise<CommercialRisk[]> {
  const { data, error } = await supabase.rpc("job_commercial_risks", { p_job_id: jobId });
  if (error) throw error;
  return (data ?? []) as CommercialRisk[];
}

export interface ClosureBlocker {
  kind: string;
  count?: number;
  amount?: number;
  detail: string;
}

export interface ClosureCheck {
  job_id: number;
  technical_complete: boolean;
  handover_signed: boolean;
  milestones: {
    final_account_agreed_at?: string | null;
    final_invoice_raised_at?: string | null;
    supplier_costs_complete_at?: string | null;
    commercially_closed_at?: string | null;
    closure_override_reason?: string | null;
    reopened_at?: string | null;
  };
  blockers: ClosureBlocker[];
}

export async function fetchClosureCheck(jobId: number): Promise<ClosureCheck> {
  const { data, error } = await supabase.rpc("job_commercial_closure_check", { p_job_id: jobId });
  if (error) throw error;
  return data as ClosureCheck;
}

export type CommercialMilestone = "final_account_agreed" | "final_invoice_raised" | "supplier_costs_complete";

export async function setCommercialMilestone(jobId: number, milestone: CommercialMilestone, done: boolean): Promise<void> {
  const { error } = await supabase.rpc("job_commercial_milestone_set", {
    p_job_id: jobId, p_milestone: milestone, p_done: done,
  });
  if (error) throw error;
}

export async function closeCommercially(jobId: number, overrideReason?: string): Promise<void> {
  const { error } = await supabase.rpc("job_commercial_close", {
    p_job_id: jobId, p_override_reason: overrideReason ?? null,
  });
  if (error) throw error;
}

export async function reopenCommercially(jobId: number): Promise<void> {
  const { error } = await supabase.rpc("job_commercial_reopen", { p_job_id: jobId });
  if (error) throw error;
}

export interface ChangeEventRow {
  id: number;
  change_type: string;
  reason_code: string | null;
  reason_text: string | null;
  quantity_delta: number | null;
  cost_impact: number | null;
  revenue_impact: number | null;
  approval_status: string;
  created_at: string;
  approved_at: string | null;
  variation_reference: string | null;
}

export async function fetchChangeEvents(jobId: number): Promise<ChangeEventRow[]> {
  const { data, error } = await supabase
    .from("project_change_events")
    .select("id, change_type, reason_code, reason_text, quantity_delta, cost_impact, revenue_impact, approval_status, created_at, approved_at, variation_reference")
    .eq("job_id", jobId)
    .is("superseded_by_event_id", null)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...r,
    quantity_delta: r.quantity_delta == null ? null : Number(r.quantity_delta),
    cost_impact: r.cost_impact == null ? null : Number(r.cost_impact),
    revenue_impact: r.revenue_impact == null ? null : Number(r.revenue_impact),
  })) as ChangeEventRow[];
}

/** The database's approval gate (0373) refuses anyone without
 * can_approve_project_commercials; the button is hidden to match. */
export async function setChangeEventDecision(id: number, approve: boolean): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("project_change_events")
    .update({
      approval_status: approve ? "approved" : "rejected",
      approved_by: auth.user?.id,
      approved_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

/* -------------------------------------- apply a room's design to its type */

export interface ApplySchematicRoomResult {
  job_room_id: number;
  name: string;
  applied: boolean;
  reason?: string;
  devices_placed?: number;
  cables_drawn?: number;
  power_set?: number;
  notes?: string[];
}

export interface ApplySchematicResult {
  job_id: number;
  from_job_room_id: number;
  room_type: string | null;
  rooms_applied: number;
  rooms: ApplySchematicRoomResult[];
}

/** 0356 + 0374: copy this room's DESIGN -- placements, drawing
 * positions, cables -- and its POWER answers onto other rooms. With no
 * target list the database finds the room's type siblings itself; a
 * room with no type passes the other rooms explicitly and the per-room
 * kit-match refusal decides which of them take the design. Either way
 * a room whose kit differs is refused with its reason, and one already
 * drawn is skipped unless told to overwrite. */
export async function applySchematicToType(
  fromJobRoomId: number,
  overwrite = false,
  targetIds?: number[],
): Promise<ApplySchematicResult> {
  const { data, error } = await supabase.rpc("job_room_apply_schematic", {
    p_from_job_room_id: fromJobRoomId,
    p_to_job_room_ids: targetIds ?? null,
    p_overwrite: overwrite,
  });
  if (error) throw error;
  return data as ApplySchematicResult;
}

/** For a room with no Room Type: which of the job's other rooms actually
 * hold the same kit, ahead of Apply -- the same test 0356's apply refuses
 * on, surfaced read-only so "Matched by kit" / Apply are only offered
 * where they would actually work rather than for any other room on the
 * job regardless of what it holds (0443). */
export async function fetchRoomKitMatchIds(jobRoomId: number): Promise<number[]> {
  const { data, error } = await supabase.rpc("job_room_kit_match_ids", { p_job_room_id: jobRoomId });
  if (error) throw error;
  return (data ?? []) as number[];
}

/* --------------------------------------------------- room-type follow/override (0384) */
//
// A separate, ongoing relationship from the one-off "apply this design"
// copy above: a room either FOLLOWS a master room's design (kept in sync
// on demand, locked against direct equipment edits) or is its OWN design
// (overridden = true, the default). Phase 1 only carries equipment
// identity/quantity; the same sync function is where a later phase widens
// to placement/ports/power/cabling.

export interface JobRoomTypeSyncResult {
  job_room_id: number;
  added: number;
  updated: number;
  removed: number;
  kept_with_commissioning_progress: number;
}

export interface JobRoomTypeStatusFollower {
  job_room_id: number;
  name: string;
  overridden: boolean;
}

export interface JobRoomTypeStatusMaster {
  master_job_room_id: number;
  master_name: string;
  followers: JobRoomTypeStatusFollower[];
}

export async function fetchJobRoomTypeStatus(jobId: number): Promise<JobRoomTypeStatusMaster[]> {
  const { data, error } = await supabase.rpc("job_room_type_status", { p_job_id: jobId });
  if (error) throw error;
  return (data ?? []) as JobRoomTypeStatusMaster[];
}

export async function linkJobRoomType(jobRoomId: number, masterJobRoomId: number): Promise<void> {
  const { error } = await supabase.rpc("job_room_type_link", {
    p_job_room_id: jobRoomId,
    p_master_job_room_id: masterJobRoomId,
  });
  if (error) throw error;
}

export async function unlinkJobRoomType(jobRoomId: number): Promise<void> {
  const { error } = await supabase.rpc("job_room_type_unlink", { p_job_room_id: jobRoomId });
  if (error) throw error;
}

/** Push the master's current design onto every room following it. */
export async function syncJobRoomType(masterJobRoomId: number): Promise<{ rooms: JobRoomTypeSyncResult[] }> {
  const { data, error } = await supabase.rpc("job_room_type_sync", { p_master_job_room_id: masterJobRoomId });
  if (error) throw error;
  return data as { rooms: JobRoomTypeSyncResult[] };
}

/** "Make this room different" -- clone the master's current design in, then stop following. */
export async function overrideJobRoomType(jobRoomId: number): Promise<JobRoomTypeSyncResult> {
  const { data, error } = await supabase.rpc("job_room_type_override", { p_job_room_id: jobRoomId });
  if (error) throw error;
  return data as JobRoomTypeSyncResult;
}

/** "Revert to room type" -- discard this room's own design and resync from the master. */
export async function revertJobRoomType(jobRoomId: number): Promise<JobRoomTypeSyncResult> {
  const { data, error } = await supabase.rpc("job_room_type_revert", { p_job_room_id: jobRoomId });
  if (error) throw error;
  return data as JobRoomTypeSyncResult;
}
